import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { menuAPI, orderAPI, dashboardAPI, tablesAPI, settingsAPI, blogAPI, staffAPI, bannersAPI } from '../utils/api';
import { formatPrice } from '../utils/translations';
import Analytics from '../components/Analytics';
// Admin tab components — loaded lazily so the initial admin bundle is lean.
// Each tab only fetches its own code chunk when the user actually opens it.
const AdminScreensTab = lazy(() => import('../components/admin/AdminScreensTab'));
const AdminDashboardOverviewTab = lazy(() => import('../components/admin/AdminDashboardOverviewTab'));
import { 
  LayoutDashboard, 
  Bell, 
  UtensilsCrossed, 
  Grid3X3, 
  BarChart3, 
  Settings, 
  ChefHat, 
  Coffee, 
  Home,
  LogOut,
  Plus,
  Printer,
  CheckCircle2,
  Clock,
  XCircle,
  Edit,
  Trash2,
  Receipt,
  CreditCard,
  Volume2,
  Upload,
  Play,
  X,
  Search,
  Monitor,
  Users,
  FileText,
  Image,
  DollarSign,
  ShoppingBag,
  Download,
  ExternalLink,
  FolderPlus,
  List,
  LayoutGrid,
  Filter,
  Star,
  Eye,
  EyeOff,
  Copy,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  Package,
  Tag,
  PenLine,
  Save,
  Globe,
  MapPin,
  ShieldAlert,
  Wallet,
  Mail
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default categories for menu
const DEFAULT_CATEGORIES = [
  "Appetizers", "Salads", "Soups", "Pho", "Seafood", "Main", "Fish", 
  "Pasta", "Curry", "Dessert", "Smoothies", "Juice", "Cocktails", 
  "Long Drinks", "Beer", "Soft Drinks", "Wine", "Hard Liquor", "Coffee"
];

const AdminDashboard = () => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [settingsTab, setSettingsTab] = useState('general');
  const [adminLang, setAdminLang] = useState('en');
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showAddTableModal, setShowAddTableModal] = useState(false);
  const [soundUploading, setSoundUploading] = useState(false);
  const [testingSound, setTestingSound] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  
  // Menu management state
  const [menuView, setMenuView] = useState('grid'); // 'grid' or 'list'
  const [menuSearch, setMenuSearch] = useState('');
  const [menuFilterCategory, setMenuFilterCategory] = useState('all');
  const [menuFilterAvailable, setMenuFilterAvailable] = useState('all');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [bulkSelected, setBulkSelected] = useState([]);
  
  // Blog management state
  const [blogPosts, setBlogPosts] = useState([]);
  const [showBlogEditor, setShowBlogEditor] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: '', content: '', excerpt: '', tags: '', published: false, author: '', read_time: '', featured: false });
  const blogImageRef = useRef(null);
  
  // QR code state
  const [qrModal, setQrModal] = useState(null); // { table_number, qr_image, order_url }
  const [qrLoading, setQrLoading] = useState(false);
  
  // Staff management state
  const [staffList, setStaffList] = useState([]);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [newStaff, setNewStaff] = useState({ username: '', password: '', display_name: '' });
  
  // Banner ads state
  const [bannerAds, setBannerAds] = useState([]);
  const [showAddBanner, setShowAddBanner] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [newBanner, setNewBanner] = useState({ title: '', image_url: '', link_url: '', link_text: '', position: 'landing', is_active: true, sort_order: 0 });

  // Finance state
  const [banksList, setBanksList] = useState([]);
  const [financeForm, setFinanceForm] = useState({ bank_code: '', bank_name: '', bank_account_number: '', bank_account_holder: '' });
  const [financeSaving, setFinanceSaving] = useState(false);
  const [financePreviewQR, setFinancePreviewQR] = useState('');
  const [financeTestAmount, setFinanceTestAmount] = useState('100000');

  // QR Cards state
  const [qrCardForm, setQrCardForm] = useState({
    restaurantName: 'Benz Eatery',
    tagline: 'Authentic Vietnamese Kitchen',
    selectedTables: [], // IDs of tables to generate QR cards for (from DB)
    wifiName: 'BenzGuest',
    orderUrl: 'benzeatery.com/order',
  });
  const [qrCardLogo, setQrCardLogo] = useState('');
  const qrCardLogoRef = useRef(null);

  // Newsletter state
  const [subscriberCount, setSubscriberCount] = useState(0);
  
  const [newItem, setNewItem] = useState({
    name: '',
    category: 'Appetizers',
    price: 0,
    image_url: '',
    ingredients: '',
    available: true,
    is_promoted: false
  });
  const [newTable, setNewTable] = useState({
    number: '',
    capacity: 4,
    is_active: true
  });

  const translations = {
    en: {
      dashboard: "Dashboard",
      orders: "Orders",
      tables: "Tables",
      menu: "Menu",
      screens: "Screens",
      reports: "Reports",
      settings: "Settings",
      revenue: "Total Revenue",
      activeOrders: "Active Orders",
      avgTime: "Avg. Time",
      guests: "Total Guests",
      logout: "Logout",
      connectedPrinters: "Printers",
      branding: "Branding",
      invoice: "Invoice/Receipt",
      general: "General",
      features: "Features",
      notifications: "Notifications",
      todayOrders: "Today's Orders",
      todayRevenue: "Today's Revenue",
      pendingOrders: "Pending Orders",
      activeTables: "Active Tables",
      noActiveOrders: "No active orders",
      addNewItem: "Add New Item",
      addNewTable: "Add New Table",
      edit: "Edit",
      delete: "Delete",
      available: "Available",
      unavailable: "Unavailable",
      activate: "Activate",
      deactivate: "Deactivate",
      saveSettings: "Update System",
      settingsSaved: "Settings saved successfully",
      cancel: "Cancel",
      startPreparing: "Start Preparing",
      markReady: "Mark Ready",
      markServed: "Mark Served",
      printReceipt: "Print Receipt",
      liveOrderActivity: "Live Order Activity",
      quickActions: "Quick Actions",
      newOrder: "NEW ORDER",
      printAll: "PRINT ALL",
      popularItems: "Popular Items Today",
      blog: "Blog",
      addPost: "Add Post",
      editPost: "Edit Post",
      blogTitle: "Title",
      blogContent: "Content (Markdown)",
      blogExcerpt: "Excerpt",
      blogTags: "Tags (comma separated)",
      published: "Published",
      draft: "Draft",
      publish: "Publish",
      unpublish: "Unpublish",
      noPosts: "No blog posts yet",
      // Sidebar
      business: "Business",
      attractions: "Attractions",
      rentals: "Rentals",
      bookings: "Bookings",
      commissions: "Commissions",
      serviceAdmin: "Service Admin",
      staff: "Staff",
      banners: "Banners",
      // Settings tabs
      finance: "Finance",
      qrCards: "QR Cards",
      reviews: "Reviews",
      maintenance: "Maintenance",
      // Finance
      vietqrTitle: "VietQR Bank Transfer",
      vietqrDesc: "Configure bank details for VietQR payment. Staff can show QR codes to customers for instant bank transfer.",
      bank: "Bank",
      selectBank: "Select Bank...",
      accountNumber: "Account Number",
      accountHolder: "Account Holder Name",
      saveBankDetails: "Save Bank Details",
      saving: "Saving...",
      testQr: "Test QR Generation",
      amount: "Amount (VND)",
      generateTestQr: "Generate Test QR",
      saveBankFirst: "Please save bank details first",
      // QR Cards
      qrCardTitle: "Table QR Card Generator",
      qrCardDesc: "Generate beautiful QR cards for each table. Print and place on tables for customers to scan and order.",
      restaurantName: "Restaurant Name",
      tagline: "Tagline",
      tablesRange: "Tables (e.g. 1-8)",
      wifiName: "Wi-Fi Name",
      orderUrl: "Order URL",
      logo: "Logo (optional)",
      changeLogo: "Change Logo",
      uploadLogo: "Upload Logo",
      removeLogo: "Remove",
      previewPrint: "Preview & Print",
      qrTip: "Pick the tables you want QR cards for — only real tables from your Tables tab are eligible. The preview opens in a new tab — use your browser's print function (Ctrl+P) to print 2 cards per A4 sheet.",
      // Reviews
      reviewsTitle: "Google & TripAdvisor Reviews",
      reviewsDesc: "Configure review ratings displayed on the public menu and suggested to customers after ordering.",
      googleReviews: "Google Reviews",
      tripadvisorReviews: "TripAdvisor Reviews",
      rating: "Rating (1-5)",
      reviewCount: "Review Count",
      reviewUrl: "Review URL",
      // Branding
      brandingDesc: "Upload your restaurant logo. It will appear on the landing page header, blog, and QR cards.",
      restaurantLogo: "Restaurant Logo",
      logoUsed: "Used in landing page header, blog pages, and QR cards",
      optimalSize: "Optimal Size",
      minSize: "Minimum Size",
      fileFormat: "File Format",
      logoSizeRec: "Logo Size Recommendations",
      squareFormat: "Square format, best for all uses",
      tooSmall: "Any smaller will appear blurry",
      transparentBg: "Transparent background preferred",
      maxFileSize: "Max file size: 5MB. Supported formats: PNG, JPG, SVG, WebP.",
      logoUploaded: "Logo uploaded successfully!",
      // Maintenance
      maintenanceMode: "Maintenance Mode",
      maintenanceDesc: "Enable maintenance mode to block public access. Admin and staff can still access the system.",
      toursAttractions: "Tours & Attractions",
      bikeRentals: "Bike Rentals",
      // Newsletter
      newsletterTitle: "Newsletter & Make.com",
      newsletterDesc: "Configure Make.com webhook for newsletter automation. Subscribers are also saved in the database.",
      makecomWebhook: "Make.com Webhook URL",
      makecomPlaceholder: "https://hook.eu2.make.com/...",
      subscriberCount: "Total Subscribers",
      repeatOrder: "Repeat Order",
      addAll: "Add All"
    },
    vi: {
      dashboard: "Tổng quan",
      orders: "Đơn hàng",
      tables: "Bàn ăn",
      menu: "Thực đơn",
      screens: "Màn hình",
      reports: "Báo cáo",
      settings: "Cài đặt",
      revenue: "Tổng doanh thu",
      activeOrders: "Đơn đang xử lý",
      avgTime: "Thời gian TB",
      guests: "Tổng khách hàng",
      logout: "Đăng xuất",
      connectedPrinters: "Máy in",
      branding: "Thương hiệu",
      invoice: "Hóa đơn/Biên lai",
      general: "Cài đặt chung",
      features: "Tính năng",
      notifications: "Thông báo",
      todayOrders: "Đơn hôm nay",
      todayRevenue: "Doanh thu hôm nay",
      pendingOrders: "Đơn chờ",
      activeTables: "Bàn hoạt động",
      noActiveOrders: "Không có đơn hàng",
      addNewItem: "Thêm món mới",
      addNewTable: "Thêm bàn mới",
      edit: "Sửa",
      delete: "Xóa",
      available: "Có sẵn",
      unavailable: "Hết",
      activate: "Kích hoạt",
      deactivate: "Vô hiệu",
      saveSettings: "Cập nhật",
      settingsSaved: "Đã lưu thành công",
      cancel: "Hủy",
      startPreparing: "Bắt đầu",
      markReady: "Sẵn sàng",
      markServed: "Đã phục vụ",
      printReceipt: "In hóa đơn",
      liveOrderActivity: "Hoạt động đơn hàng",
      quickActions: "Thao tác nhanh",
      newOrder: "ĐƠN MỚI",
      printAll: "IN TẤT CẢ",
      popularItems: "Món phổ biến hôm nay",
      blog: "Blog",
      addPost: "Thêm bài",
      editPost: "Sửa bài",
      blogTitle: "Tiêu đề",
      blogContent: "Nội dung (Markdown)",
      blogExcerpt: "Tóm tắt",
      blogTags: "Thẻ (phân cách bằng dấu phẩy)",
      published: "Đã đăng",
      draft: "Nháp",
      publish: "Đăng",
      unpublish: "Gỡ đăng",
      noPosts: "Chưa có bài viết nào",
      // Sidebar
      business: "Kinh doanh",
      attractions: "Điểm tham quan",
      rentals: "Cho thuê",
      bookings: "Đặt chỗ",
      commissions: "Hoa hồng",
      serviceAdmin: "Quản trị dịch vụ",
      staff: "Nhân viên",
      banners: "Banner",
      // Settings tabs
      finance: "Tài chính",
      qrCards: "Thẻ QR",
      reviews: "Đánh giá",
      maintenance: "Bảo trì",
      // Finance
      vietqrTitle: "Chuyển khoản VietQR",
      vietqrDesc: "Cấu hình thông tin ngân hàng cho thanh toán VietQR. Nhân viên có thể hiển thị mã QR cho khách hàng.",
      bank: "Ngân hàng",
      selectBank: "Chọn ngân hàng...",
      accountNumber: "Số tài khoản",
      accountHolder: "Tên chủ tài khoản",
      saveBankDetails: "Lưu thông tin ngân hàng",
      saving: "Đang lưu...",
      testQr: "Tạo QR thử nghiệm",
      amount: "Số tiền (VND)",
      generateTestQr: "Tạo QR thử",
      saveBankFirst: "Vui lòng lưu thông tin ngân hàng trước",
      // QR Cards
      qrCardTitle: "Tạo thẻ QR cho bàn",
      qrCardDesc: "Tạo thẻ QR đẹp cho từng bàn. In và đặt trên bàn để khách hàng quét và đặt món.",
      restaurantName: "Tên nhà hàng",
      tagline: "Khẩu hiệu",
      tablesRange: "Bàn (vd: 1-8)",
      wifiName: "Tên Wi-Fi",
      orderUrl: "URL đặt món",
      logo: "Logo (tùy chọn)",
      changeLogo: "Đổi logo",
      uploadLogo: "Tải logo lên",
      removeLogo: "Xóa",
      previewPrint: "Xem trước & In",
      qrTip: "Chọn các bàn bạn muốn tạo thẻ QR — chỉ các bàn thật trong tab Bàn mới khả dụng. Bản xem trước mở trong tab mới — dùng chức năng in của trình duyệt (Ctrl+P) để in 2 thẻ mỗi trang A4.",
      // Reviews
      reviewsTitle: "Đánh giá Google & TripAdvisor",
      reviewsDesc: "Cấu hình xếp hạng đánh giá hiển thị trên thực đơn công khai và gợi ý cho khách hàng sau khi đặt món.",
      googleReviews: "Đánh giá Google",
      tripadvisorReviews: "Đánh giá TripAdvisor",
      rating: "Xếp hạng (1-5)",
      reviewCount: "Số lượng đánh giá",
      reviewUrl: "URL đánh giá",
      // Branding
      brandingDesc: "Tải lên logo nhà hàng. Logo sẽ xuất hiện trên tiêu đề trang chủ, blog và thẻ QR.",
      restaurantLogo: "Logo nhà hàng",
      logoUsed: "Dùng trong tiêu đề trang chủ, blog và thẻ QR",
      optimalSize: "Kích thước tối ưu",
      minSize: "Kích thước tối thiểu",
      fileFormat: "Định dạng tệp",
      logoSizeRec: "Khuyến nghị kích thước logo",
      squareFormat: "Định dạng vuông, tốt nhất cho mọi mục đích",
      tooSmall: "Nhỏ hơn sẽ bị mờ",
      transparentBg: "Nền trong suốt được ưu tiên",
      maxFileSize: "Kích thước tối đa: 5MB. Định dạng: PNG, JPG, SVG, WebP.",
      logoUploaded: "Tải logo thành công!",
      // Maintenance
      maintenanceMode: "Chế độ bảo trì",
      maintenanceDesc: "Bật chế độ bảo trì để chặn truy cập công khai. Admin và nhân viên vẫn có thể truy cập.",
      toursAttractions: "Tour & Diem tham quan",
      bikeRentals: "Cho thue xe dap",
      // Newsletter
      newsletterTitle: "Ban tin & Make.com",
      newsletterDesc: "Cau hinh webhook Make.com de tu dong hoa ban tin. Nguoi dang ky cung duoc luu trong co so du lieu.",
      makecomWebhook: "URL Webhook Make.com",
      makecomPlaceholder: "https://hook.eu2.make.com/...",
      subscriberCount: "Tong nguoi dang ky",
      repeatOrder: "Dat lai",
      addAll: "Them tat ca"
    }
  };

  const t = translations[adminLang];

  useEffect(() => {
    loadData();
    const interval = setInterval(loadOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    await Promise.all([loadOrders(), loadMenu(), loadTables(), loadSettings(), loadStats(), loadBlog(), loadStaff(), loadBanners(), loadFinance()]);
    setLoading(false);
  };

  const loadOrders = async () => {
    try {
      const response = await orderAPI.getAll();
      setOrders(response.data);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadMenu = async () => {
    try {
      const response = await menuAPI.getAll();
      setMenuItems(response.data);
      // Extract unique categories from menu items
      const uniqueCats = [...new Set(response.data.map(item => item.category))];
      // Merge with default categories and remove duplicates
      const allCats = [...new Set([...uniqueCats, ...DEFAULT_CATEGORIES])];
      setCategories(allCats.sort());
      // Initialize expanded state for all categories
      const expanded = {};
      uniqueCats.forEach(cat => { expanded[cat] = true; });
      setExpandedCategories(expanded);
    } catch (error) {
      console.error('Error loading menu:', error);
    }
  };

  const loadTables = async () => {
    try {
      const response = await tablesAPI.getAll();
      setTables(response.data);
    } catch (error) {
      console.error('Error loading tables:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Blog functions
  const loadBlog = async () => {
    try {
      const response = await blogAPI.getAll(false);
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error loading blog posts:', error);
    }
  };

  const loadStaff = async () => {
    try {
      const response = await staffAPI.list();
      setStaffList(response.data);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const createStaffAccount = async () => {
    if (!newStaff.username.trim() || !newStaff.password.trim()) {
      alert('Username and password are required');
      return;
    }
    try {
      await staffAPI.create(newStaff);
      setShowAddStaff(false);
      setNewStaff({ username: '', password: '', display_name: '' });
      await loadStaff();
      alert('Staff account created');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create staff');
    }
  };

  const deleteStaffAccount = async (id) => {
    if (!window.confirm('Delete this staff account?')) return;
    try {
      await staffAPI.delete(id);
      await loadStaff();
    } catch (error) {
      alert('Failed to delete staff');
    }
  };

  const toggleStaffActive = async (id) => {
    try {
      await staffAPI.toggle(id);
      await loadStaff();
    } catch (error) {
      alert('Failed to update staff');
    }
  };

  const resetStaffPassword = async (id) => {
    const newPwd = prompt('Enter new password (min 4 chars):');
    if (!newPwd || newPwd.length < 4) return;
    try {
      await staffAPI.resetPassword(id, newPwd);
      alert('Password updated');
    } catch (error) {
      alert('Failed to reset password');
    }
  };

  // Service admin functions
  // Dead sections removed: loadServiceAdmins, createServiceAdminAccount,
  // deleteServiceAdminAccount, toggleServiceAdminActive, resetServiceAdminPassword,
  // loadCommissions, recordCommPayment, deleteCommPayment
  // (Business / Partner / Service-Admin tabs were disabled upstream.)

  // Finance
  const loadFinance = async () => {
    try {
      const [banksRes, settingsRes, subCountRes] = await Promise.all([
        axios.get(`${API_URL}/api/banks`),
        settingsAPI.get(),
        axios.get(`${API_URL}/api/newsletter/count`).catch(() => ({ data: { count: 0 } })),
      ]);
      setBanksList(banksRes.data);
      setSubscriberCount(subCountRes.data?.count || 0);
      const s = settingsRes.data;
      setFinanceForm({
        bank_code: s.bank_code || '',
        bank_name: s.bank_name || '',
        bank_account_number: s.bank_account_number || '',
        bank_account_holder: s.bank_account_holder || '',
      });
      if (s.bank_code && s.bank_account_number) {
        setFinancePreviewQR(`https://img.vietqr.io/image/${s.bank_code}-${s.bank_account_number}-compact2.png?amount=100000&addInfo=Preview&accountName=${encodeURIComponent(s.bank_account_holder || '')}`);
      }
    } catch (error) {
      console.error('Error loading finance:', error);
    }
  };

  const saveFinance = async () => {
    setFinanceSaving(true);
    try {
      const selectedBank = banksList.find(b => b.code === financeForm.bank_code);
      const updateData = {
        bank_code: financeForm.bank_code,
        bank_name: selectedBank?.name || financeForm.bank_name,
        bank_account_number: financeForm.bank_account_number,
        bank_account_holder: financeForm.bank_account_holder,
      };
      await settingsAPI.update(updateData);
      if (financeForm.bank_code && financeForm.bank_account_number) {
        setFinancePreviewQR(`https://img.vietqr.io/image/${financeForm.bank_code}-${financeForm.bank_account_number}-compact2.png?amount=100000&addInfo=Preview&accountName=${encodeURIComponent(financeForm.bank_account_holder || '')}`);
      }
      await loadSettings();
    } catch (error) {
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    }
    setFinanceSaving(false);
  };

  const loadBanners = async () => {
    try {
      const response = await bannersAPI.getAll();
      setBannerAds(response.data);
    } catch (error) {
      console.error('Error loading banners:', error);
    }
  };

  const saveBanner = async () => {
    if (!newBanner.title.trim()) { alert('Title is required'); return; }
    try {
      if (editingBanner) {
        await bannersAPI.update(editingBanner.id, newBanner);
      } else {
        await bannersAPI.create(newBanner);
      }
      setShowAddBanner(false);
      setEditingBanner(null);
      setNewBanner({ title: '', image_url: '', link_url: '', link_text: '', position: 'landing', is_active: true, sort_order: 0 });
      await loadBanners();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save banner');
    }
  };

  const deleteBannerAd = async (id) => {
    if (!window.confirm('Delete this banner ad?')) return;
    try {
      await bannersAPI.delete(id);
      await loadBanners();
    } catch (error) {
      alert('Failed to delete banner');
    }
  };

  const toggleBannerActive = async (banner) => {
    try {
      await bannersAPI.update(banner.id, { is_active: !banner.is_active });
      await loadBanners();
    } catch (error) {
      alert('Failed to update banner');
    }
  };

  const uploadBannerImage = async (bannerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await bannersAPI.uploadImage(bannerId, formData);
      await loadBanners();
    } catch (error) {
      alert('Failed to upload image');
    }
  };

  const openBannerEditor = (banner = null) => {
    if (banner) {
      setEditingBanner(banner);
      setNewBanner({ title: banner.title, image_url: banner.image_url || '', link_url: banner.link_url || '', link_text: banner.link_text || '', position: banner.position || 'landing', is_active: banner.is_active, sort_order: banner.sort_order || 0 });
    } else {
      setEditingBanner(null);
      setNewBanner({ title: '', image_url: '', link_url: '', link_text: '', position: 'landing', is_active: true, sort_order: 0 });
    }
    setShowAddBanner(true);
  };

  // Attraction / Rental / Booking / Partner handlers removed — those tabs are
  // disabled in restaurant-only mode. State variables remain in place
  // (lightweight useState) but no loaders or editors are wired up.

  const openBlogEditor = (post = null) => {
    if (post) {
      setEditingBlog(post);
      setBlogForm({
        title: post.title,
        content: post.content,
        excerpt: post.excerpt || '',
        tags: (post.tags || []).join(', '),
        published: post.published,
        author: post.author || '',
        read_time: post.read_time || '',
        featured: post.featured || false,
      });
    } else {
      setEditingBlog(null);
      setBlogForm({ title: '', content: '', excerpt: '', tags: '', published: false, author: '', read_time: '', featured: false });
    }
    setShowBlogEditor(true);
  };

  const saveBlogPost = async () => {
    if (!blogForm.title.trim() || !blogForm.content.trim()) {
      alert('Title and content are required');
      return;
    }
    const payload = {
      title: blogForm.title,
      content: blogForm.content,
      excerpt: blogForm.excerpt || blogForm.content.substring(0, 200),
      tags: blogForm.tags.split(',').map(t => t.trim()).filter(Boolean),
      published: blogForm.published,
      author: blogForm.author,
      read_time: blogForm.read_time,
      featured: blogForm.featured,
    };
    try {
      if (editingBlog) {
        await blogAPI.update(editingBlog.id, payload);
      } else {
        await blogAPI.create(payload);
      }
      setShowBlogEditor(false);
      await loadBlog();
    } catch (error) {
      console.error('Error saving blog post:', error);
      alert('Failed to save blog post');
    }
  };

  const deleteBlogPost = async (postId) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await blogAPI.delete(postId);
      await loadBlog();
    } catch (error) {
      console.error('Error deleting blog post:', error);
    }
  };

  const toggleBlogPublish = async (post) => {
    try {
      await blogAPI.update(post.id, { published: !post.published });
      await loadBlog();
    } catch (error) {
      console.error('Error toggling publish:', error);
    }
  };

  const uploadBlogCoverImage = async (postId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await blogAPI.uploadImage(postId, formData);
      await loadBlog();
    } catch (error) {
      console.error('Error uploading blog image:', error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await orderAPI.updateStatus(orderId, newStatus);
      await loadOrders();
      await loadStats();
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    }
  };

  const deleteMenuItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await menuAPI.delete(itemId);
      await loadMenu();
      alert('Item deleted successfully');
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item');
    }
  };

  const toggleItemAvailability = async (item) => {
    try {
      await menuAPI.update(item.id, { available: !item.available });
      await loadMenu();
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item');
    }
  };

  const toggleItemPromotion = async (item) => {
    try {
      await menuAPI.update(item.id, { is_promoted: !item.is_promoted });
      await loadMenu();
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item promotion');
    }
  };

  const saveNewItem = async () => {
    try {
      await menuAPI.create(newItem);
      setShowAddItemModal(false);
      setNewItem({ name: '', category: 'Appetizers', price: 0, image_url: '', ingredients: '', available: true, is_promoted: false });
      await loadMenu();
      alert('Item added successfully');
    } catch (error) {
      console.error('Error creating item:', error);
      alert('Failed to add item');
    }
  };

  const updateMenuItem = async () => {
    try {
      await menuAPI.update(editingItem.id, editingItem);
      setEditingItem(null);
      await loadMenu();
      alert('Item updated successfully');
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item');
    }
  };

  // Image upload for menu item
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingItem) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload JPG, PNG, WebP, or GIF.');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB.');
      return;
    }
    
    setImageUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/menu/${editingItem.id}/upload-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        setEditingItem({...editingItem, image_url: response.data.image_url});
        await loadMenu();
        alert('Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    }
    
    setImageUploading(false);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // Bulk operations
  const toggleBulkSelect = (itemId) => {
    setBulkSelected(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const selectAllInCategory = (category) => {
    const categoryItems = menuItems.filter(item => item.category === category).map(item => item.id);
    setBulkSelected(prev => {
      const allSelected = categoryItems.every(id => prev.includes(id));
      if (allSelected) {
        return prev.filter(id => !categoryItems.includes(id));
      }
      return [...new Set([...prev, ...categoryItems])];
    });
  };

  const bulkToggleAvailability = async (available) => {
    if (bulkSelected.length === 0) return;
    try {
      await Promise.all(bulkSelected.map(id => 
        menuAPI.update(id, { available })
      ));
      await loadMenu();
      setBulkSelected([]);
      alert(`${bulkSelected.length} items updated`);
    } catch (error) {
      console.error('Bulk update error:', error);
      alert('Failed to update items');
    }
  };

  const bulkDelete = async () => {
    if (bulkSelected.length === 0) return;
    if (!window.confirm(`Delete ${bulkSelected.length} items? This cannot be undone.`)) return;
    try {
      await Promise.all(bulkSelected.map(id => menuAPI.delete(id)));
      await loadMenu();
      setBulkSelected([]);
      alert('Items deleted');
    } catch (error) {
      console.error('Bulk delete error:', error);
      alert('Failed to delete items');
    }
  };

  const duplicateItem = async (item) => {
    try {
      const newItemData = {
        name: item.name.en + ' (Copy)',
        category: item.category,
        price: item.price,
        image_url: item.image_url,
        ingredients: item.ingredients,
        available: item.available,
        is_promoted: false
      };
      await menuAPI.create(newItemData);
      await loadMenu();
      alert('Item duplicated');
    } catch (error) {
      console.error('Duplicate error:', error);
      alert('Failed to duplicate item');
    }
  };

  // Category management
  const addCategory = () => {
    if (!newCategoryName.trim()) return;
    if (categories.includes(newCategoryName.trim())) {
      alert('Category already exists');
      return;
    }
    setCategories(prev => [...prev, newCategoryName.trim()].sort());
    setNewCategoryName('');
    alert('Category added');
  };

  const renameCategory = async (oldName, newName) => {
    if (!newName.trim() || oldName === newName) return;
    try {
      // Update all items with this category
      const itemsToUpdate = menuItems.filter(item => item.category === oldName);
      await Promise.all(itemsToUpdate.map(item => 
        menuAPI.update(item.id, { category: newName })
      ));
      await loadMenu();
      setEditingCategory(null);
      alert('Category renamed');
    } catch (error) {
      console.error('Rename error:', error);
      alert('Failed to rename category');
    }
  };

  const deleteCategory = async (categoryName) => {
    const itemsInCategory = menuItems.filter(item => item.category === categoryName);
    if (itemsInCategory.length > 0) {
      if (!window.confirm(`This will delete ${itemsInCategory.length} items in "${categoryName}". Continue?`)) return;
      try {
        await Promise.all(itemsInCategory.map(item => menuAPI.delete(item.id)));
        await loadMenu();
        setCategories(prev => prev.filter(c => c !== categoryName));
        alert('Category and items deleted');
      } catch (error) {
        alert('Failed to delete');
      }
    } else {
      setCategories(prev => prev.filter(c => c !== categoryName));
      alert('Empty category removed');
    }
  };

  // Filter menu items
  const filteredMenuItems = menuItems.filter(item => {
    const matchesSearch = item.name.en.toLowerCase().includes(menuSearch.toLowerCase()) ||
                         item.category.toLowerCase().includes(menuSearch.toLowerCase());
    const matchesCategory = menuFilterCategory === 'all' || item.category === menuFilterCategory;
    const matchesAvailable = menuFilterAvailable === 'all' || 
                            (menuFilterAvailable === 'available' && item.available) ||
                            (menuFilterAvailable === 'unavailable' && !item.available);
    return matchesSearch && matchesCategory && matchesAvailable;
  });

  const deleteTable = async (tableId) => {
    if (!window.confirm('Are you sure you want to delete this table?')) return;
    try {
      await tablesAPI.delete(tableId);
      await loadTables();
      alert('Table deleted successfully');
    } catch (error) {
      console.error('Error deleting table:', error);
      alert('Failed to delete table');
    }
  };

  const toggleTableStatus = async (table) => {
    try {
      await tablesAPI.update(table.id, { is_active: !table.is_active });
      await loadTables();
    } catch (error) {
      console.error('Error updating table:', error);
      alert('Failed to update table');
    }
  };

  const showTableQR = async (table) => {
    setQrLoading(true);
    try {
      const baseUrl = window.location.origin;
      const response = await tablesAPI.getQR(table.id, baseUrl);
      setQrModal(response.data);
    } catch (error) {
      console.error('Error generating QR:', error);
      alert('Failed to generate QR code');
    }
    setQrLoading(false);
  };

  const downloadQR = (tableNumber, qrImage) => {
    const link = document.createElement('a');
    link.href = qrImage;
    link.download = `table-${tableNumber}-qr.png`;
    link.click();
  };

  const printQR = (tableNumber, qrImage, orderUrl) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head><title>Table ${tableNumber} QR Code</title>
        <style>
          body { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:100vh; margin:0; font-family:Georgia,serif; }
          img { width:300px; height:300px; }
          h1 { font-size:48px; margin:0 0 8px; }
          h2 { font-size:20px; margin:0 0 24px; color:#666; font-weight:normal; }
          p { font-size:14px; color:#999; margin-top:16px; }
          @media print { body { padding:0; } }
        </style>
        </head>
        <body>
          <h1>Table ${tableNumber}</h1>
          <h2>Benz Eatery</h2>
          <img src="${qrImage}" alt="QR Code" />
          <p>Scan to view menu & order</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
  };

  const saveNewTable = async () => {
    if (!newTable.number.trim()) {
      alert('Please enter table number');
      return;
    }
    try {
      await tablesAPI.create(newTable);
      setShowAddTableModal(false);
      setNewTable({ number: '', capacity: 4, is_active: true });
      await loadTables();
      alert('Table added successfully');
    } catch (error) {
      console.error('Error creating table:', error);
      alert(error.response?.data?.detail || 'Failed to add table');
    }
  };

  const updateTable = async () => {
    try {
      await tablesAPI.update(editingTable.id, editingTable);
      setEditingTable(null);
      await loadTables();
      alert('Table updated successfully');
    } catch (error) {
      console.error('Error updating table:', error);
      alert('Failed to update table');
    }
  };

  const saveSettings = async () => {
    try {
      await settingsAPI.update(settings);
      await loadSettings();
      alert(t.settingsSaved);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      preparing: 'bg-blue-100 text-blue-700',
      ready: 'bg-emerald-100 text-emerald-700',
      served: 'bg-slate-100 text-slate-600',
      cancelled: 'bg-red-100 text-red-700'
    };
    return styles[status] || styles.pending;
  };

  const [businessOpen, setBusinessOpen] = useState(false);
  const businessTabs = []; // Business section disabled — restaurant-only mode

  const navItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: t.dashboard },
    { id: 'orders', icon: Bell, label: t.orders },
    { id: 'tables', icon: Grid3X3, label: t.tables },
    { id: 'menu', icon: UtensilsCrossed, label: t.menu },
    { id: 'staff', icon: Users, label: t.staff },
    { id: 'banners', icon: Image, label: t.banners },
    { id: 'blog', icon: PenLine, label: t.blog },
    { id: 'screens', icon: Monitor, label: t.screens },
    { id: 'reports', icon: BarChart3, label: t.reports },
    { id: 'settings', icon: Settings, label: t.settings }
  ];

  const settingsOptions = [
    { id: 'general', label: t.general, icon: Settings },
    { id: 'landing', label: adminLang === 'en' ? 'Landing Page' : 'Trang chủ', icon: Globe },
    { id: 'printers', label: t.connectedPrinters, icon: Printer },
    { id: 'invoice', label: t.invoice, icon: FileText },
    { id: 'notifications', label: t.notifications, icon: Volume2 },
    { id: 'features', label: t.features, icon: Settings },
    { id: 'branding', label: t.branding, icon: Image },
    { id: 'finance', label: t.finance, icon: Wallet },
    { id: 'qrcards', label: t.qrCards, icon: Grid3X3 },
    { id: 'reviews', label: t.reviews, icon: Star },
    { id: 'newsletter', label: t.newsletterTitle?.split(' &')[0] || 'Newsletter', icon: Mail },
    { id: 'maintenance', label: t.maintenance, icon: ShieldAlert },
  ];

  // Dashboard Stats Cards
  const statsData = stats ? [
    { label: t.todayRevenue, val: formatPrice(stats.total_revenue_today), icon: DollarSign, color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: t.activeOrders, val: orders.filter(o => ['pending', 'preparing', 'ready'].includes(o.status)).length, icon: ShoppingBag, color: "text-blue-600", bg: "bg-blue-50" },
    { label: t.avgTime, val: "18 mins", icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
    { label: t.activeTables, val: stats.active_tables?.length || 0, icon: Users, color: "text-indigo-600", bg: "bg-indigo-50" },
  ] : [];

  // Render Dashboard — extracted to components/admin/AdminDashboardOverviewTab.js
  const renderDashboard = () => (
    <AdminDashboardOverviewTab
      stats={stats}
      orders={orders}
      statsData={statsData}
      t={t}
      setActiveTab={setActiveTab}
      getStatusBadge={getStatusBadge}
    />
  );

  // Render Orders
  const renderOrders = () => {
    const activeOrders = orders.filter(o => ['pending', 'preparing', 'ready'].includes(o.status));
    const completedOrders = orders.filter(o => ['served', 'cancelled'].includes(o.status));

    return (
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></span>
          <h2 className="text-xl font-bold text-slate-900">{t.activeOrders} ({activeOrders.length})</h2>
        </div>
        
        {activeOrders.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <p className="text-slate-400 font-medium">{t.noActiveOrders}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeOrders.map(order => (
              <div key={order.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow" data-testid={`order-${order.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl font-bold text-slate-900">#{order.order_number}</span>
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${getStatusBadge(order.status)}`}>
                        {order.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">
                      Table <span className="font-bold text-indigo-600">{order.table}</span> • {new Date(order.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{formatPrice(order.total)}</p>
                </div>

                <div className="mb-4 space-y-2">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between items-start py-2 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="font-medium text-slate-700">
                          <span className="text-indigo-600 mr-2">×{item.quantity}</span>
                          {item.name.en}
                        </p>
                        {item.special_note && (
                          <p className="text-xs text-blue-600 mt-1 italic">Note: {item.special_note}</p>
                        )}
                      </div>
                      <span className="text-sm font-medium text-slate-600">{formatPrice(item.price * item.quantity)}</span>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  {order.status === 'pending' && (
                    <button onClick={() => updateOrderStatus(order.id, 'preparing')} className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors">
                      {t.startPreparing}
                    </button>
                  )}
                  {order.status === 'preparing' && (
                    <button onClick={() => updateOrderStatus(order.id, 'ready')} className="flex-1 bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition-colors">
                      {t.markReady}
                    </button>
                  )}
                  {order.status === 'ready' && (
                    <button onClick={() => updateOrderStatus(order.id, 'served')} className="flex-1 bg-slate-700 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors">
                      {t.markServed}
                    </button>
                  )}
                  <button onClick={() => window.open(`${API_URL}/api/orders/${order.id}/receipt?autoprint=true`, '_blank')} className="px-4 bg-slate-100 text-slate-700 py-3 rounded-xl font-bold hover:bg-slate-200 transition-colors" title={t.printReceipt}>
                    <Printer className="w-5 h-5" />
                  </button>
                  <button onClick={() => updateOrderStatus(order.id, 'cancelled')} className="px-4 bg-red-100 text-red-600 py-3 rounded-xl font-bold hover:bg-red-200 transition-colors">
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {completedOrders.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-bold text-slate-600 mb-4">Completed Orders</h3>
            <div className="space-y-2">
              {completedOrders.slice(0, 10).map(order => (
                <div key={order.id} className="bg-slate-50 rounded-xl p-4 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-slate-600">#{order.order_number}</span>
                    <span className="text-slate-400">•</span>
                    <span className="text-sm text-slate-500">Table {order.table}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusBadge(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                  <span className="font-bold text-slate-600">{formatPrice(order.total)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Menu - Full Featured
  const renderMenu = () => {
    const menuCategories = [...new Set(menuItems.map(item => item.category))];
    const availableCount = menuItems.filter(i => i.available).length;
    const promotedCount = menuItems.filter(i => i.is_promoted).length;

    return (
      <div className="space-y-6">
        {/* Header Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-slate-500 text-xs font-bold uppercase">Total Items</p>
            <p className="text-2xl font-black text-slate-900">{menuItems.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-slate-500 text-xs font-bold uppercase">Categories</p>
            <p className="text-2xl font-black text-slate-900">{menuCategories.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-slate-500 text-xs font-bold uppercase">Available</p>
            <p className="text-2xl font-black text-emerald-600">{availableCount}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-slate-500 text-xs font-bold uppercase">Must Try</p>
            <p className="text-2xl font-black text-amber-500">{promotedCount}</p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder={adminLang === 'en' ? 'Search menu items...' : 'Tìm món...'}
                value={menuSearch}
                onChange={(e) => setMenuSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2">
              <select
                value={menuFilterCategory}
                onChange={(e) => setMenuFilterCategory(e.target.value)}
                className="px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium"
              >
                <option value="all">{adminLang === 'en' ? 'All Categories' : 'Tất cả'}</option>
                {menuCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              
              <select
                value={menuFilterAvailable}
                onChange={(e) => setMenuFilterAvailable(e.target.value)}
                className="px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium"
              >
                <option value="all">{adminLang === 'en' ? 'All Status' : 'Tất cả'}</option>
                <option value="available">{adminLang === 'en' ? 'Available' : 'Có sẵn'}</option>
                <option value="unavailable">{adminLang === 'en' ? 'Unavailable' : 'Hết'}</option>
              </select>

              {/* View Toggle */}
              <div className="flex bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setMenuView('grid')}
                  className={`p-2 rounded-lg ${menuView === 'grid' ? 'bg-white shadow-sm' : ''}`}
                >
                  <LayoutGrid size={18} className={menuView === 'grid' ? 'text-indigo-600' : 'text-slate-400'} />
                </button>
                <button
                  onClick={() => setMenuView('list')}
                  className={`p-2 rounded-lg ${menuView === 'list' ? 'bg-white shadow-sm' : ''}`}
                >
                  <List size={18} className={menuView === 'list' ? 'text-indigo-600' : 'text-slate-400'} />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCategoryModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-purple-100 text-purple-700 rounded-xl font-bold text-sm hover:bg-purple-200"
              >
                <FolderPlus size={18} />
                {adminLang === 'en' ? 'Categories' : 'Danh mục'}
              </button>
              <button
                onClick={() => setShowAddItemModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700"
                data-testid="add-item-btn"
              >
                <Plus size={18} />
                {t.addNewItem}
              </button>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {bulkSelected.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm font-bold text-slate-600">
                {bulkSelected.length} item{bulkSelected.length > 1 ? 's' : ''} selected
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => bulkToggleAvailability(true)}
                  className="px-3 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold hover:bg-emerald-200"
                >
                  <Eye size={14} className="inline mr-1" /> Enable All
                </button>
                <button
                  onClick={() => bulkToggleAvailability(false)}
                  className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg text-xs font-bold hover:bg-amber-200"
                >
                  <EyeOff size={14} className="inline mr-1" /> Disable All
                </button>
                <button
                  onClick={bulkDelete}
                  className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-xs font-bold hover:bg-red-200"
                >
                  <Trash2 size={14} className="inline mr-1" /> Delete
                </button>
                <button
                  onClick={() => setBulkSelected([])}
                  className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold hover:bg-slate-200"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Menu Items */}
        {menuView === 'list' ? (
          // List View - Grouped by Category
          <div className="space-y-4">
            {menuCategories.filter(cat => 
              menuFilterCategory === 'all' || cat === menuFilterCategory
            ).map(category => {
              const categoryItems = filteredMenuItems.filter(item => item.category === category);
              if (categoryItems.length === 0) return null;
              
              return (
                <div key={category} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  {/* Category Header */}
                  <div 
                    className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 cursor-pointer"
                    onClick={() => setExpandedCategories(prev => ({...prev, [category]: !prev[category]}))}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={categoryItems.every(item => bulkSelected.includes(item.id))}
                        onChange={() => selectAllInCategory(category)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                      />
                      <Tag size={16} className="text-slate-400" />
                      <span className="font-bold text-slate-700">{category}</span>
                      <span className="text-xs text-slate-400 bg-slate-200 px-2 py-0.5 rounded-full">
                        {categoryItems.length} items
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingCategory(category); }}
                        className="p-1 text-slate-400 hover:text-slate-600"
                      >
                        <Edit size={14} />
                      </button>
                      {expandedCategories[category] !== false ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </div>

                  {/* Category Items */}
                  {expandedCategories[category] !== false && (
                    <div className="divide-y divide-slate-100">
                      {categoryItems.map(item => (
                        <div key={item.id} className={`flex items-center gap-4 px-4 py-3 hover:bg-slate-50 ${!item.available ? 'opacity-50' : ''}`}>
                          <input
                            type="checkbox"
                            checked={bulkSelected.includes(item.id)}
                            onChange={() => toggleBulkSelect(item.id)}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600"
                          />
                          <img src={item.image_url} alt={item.name.en} className="w-12 h-12 rounded-lg object-cover" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-slate-900 truncate">{item.name.en}</p>
                              {item.is_promoted && <Star size={14} className="text-amber-500 fill-amber-500" />}
                            </div>
                            <p className="text-xs text-slate-400 truncate">{item.ingredients || 'No ingredients listed'}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-indigo-600">{formatPrice(item.price)}</span>
                            <button onClick={() => toggleItemAvailability(item)} className={`p-2 rounded-lg ${item.available ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {item.available ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                            <button onClick={() => toggleItemPromotion(item)} className={`p-2 rounded-lg ${item.is_promoted ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'}`}>
                              <Star size={16} className={item.is_promoted ? 'fill-current' : ''} />
                            </button>
                            <button onClick={() => duplicateItem(item)} className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200">
                              <Copy size={16} />
                            </button>
                            <button onClick={() => setEditingItem({ ...item })} className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">
                              <Edit size={16} />
                            </button>
                            <button onClick={() => deleteMenuItem(item.id)} className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          // Grid View
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMenuItems.map(item => (
              <div 
                key={item.id} 
                className={`bg-white border rounded-2xl overflow-hidden hover:shadow-lg transition-shadow ${
                  item.available ? 'border-slate-200' : 'border-red-200 opacity-60'
                } ${item.is_promoted ? 'ring-2 ring-amber-300' : ''} ${
                  bulkSelected.includes(item.id) ? 'ring-2 ring-indigo-500' : ''
                }`}
                data-testid={`menu-item-${item.id}`}
              >
                <div className="relative">
                  <img src={item.image_url} alt={item.name.en} className="w-full h-40 object-cover" />
                  {/* Selection Checkbox */}
                  <div className="absolute top-2 left-2">
                    <input
                      type="checkbox"
                      checked={bulkSelected.includes(item.id)}
                      onChange={() => toggleBulkSelect(item.id)}
                      className="w-5 h-5 rounded border-2 border-white shadow-lg text-indigo-600 cursor-pointer"
                    />
                  </div>
                  {/* Status Badges */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {item.is_promoted && (
                      <span className="bg-amber-500 text-white text-[10px] font-black px-2 py-1 rounded-full">MUST TRY</span>
                    )}
                    {!item.available && (
                      <span className="bg-red-500 text-white text-[10px] font-black px-2 py-1 rounded-full">SOLD OUT</span>
                    )}
                  </div>
                  {/* Category Tag */}
                  <div className="absolute bottom-2 left-2">
                    <span className="bg-black/70 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                      {item.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-bold text-slate-900 line-clamp-1">{item.name.en}</h4>
                    <p className="text-lg font-black text-indigo-600">{formatPrice(item.price)}</p>
                  </div>
                  
                  {item.ingredients && (
                    <p className="text-xs text-slate-400 mb-3 line-clamp-1">{item.ingredients}</p>
                  )}

                  <div className="flex gap-1.5">
                    <button 
                      onClick={() => toggleItemAvailability(item)} 
                      className={`flex-1 py-2 rounded-lg text-xs font-bold ${item.available ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                    >
                      {item.available ? 'Available' : 'Sold Out'}
                    </button>
                    <button onClick={() => toggleItemPromotion(item)} className={`p-2 rounded-lg ${item.is_promoted ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'}`}>
                      <Star size={16} className={item.is_promoted ? 'fill-current' : ''} />
                    </button>
                    <button onClick={() => duplicateItem(item)} className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200">
                      <Copy size={16} />
                    </button>
                    <button onClick={() => setEditingItem({ ...item })} className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">
                      <Edit size={16} />
                    </button>
                    <button onClick={() => deleteMenuItem(item.id)} className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredMenuItems.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <Package size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-400 font-medium">
              {menuSearch || menuFilterCategory !== 'all' || menuFilterAvailable !== 'all'
                ? 'No items match your filters'
                : 'No menu items yet. Click "Add New Item" to get started.'
              }
            </p>
          </div>
        )}
      </div>
    );
  };

  // Render Tables
  const renderTables = () => (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-900">{t.tables}</h2>
        <button onClick={() => setShowAddTableModal(true)} className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-colors" data-testid="add-table-btn">
          <Plus className="w-5 h-5" />
          {t.addNewTable}
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tables.map(table => (
          <div key={table.id} className={`bg-white rounded-xl p-5 border ${table.is_active ? 'border-slate-200' : 'border-red-200 opacity-60'}`} data-testid={`table-${table.id}`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-2xl font-bold text-slate-900">{table.number}</h3>
                <p className="text-sm text-slate-500">Capacity: {table.capacity} seats</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${table.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {table.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => showTableQR(table)}
                className="flex-1 px-3 py-2 rounded-lg text-xs font-bold bg-violet-100 text-violet-700 hover:bg-violet-200 flex items-center justify-center gap-1"
                data-testid={`table-qr-btn-${table.id}`}
              >
                <Grid3X3 className="w-3.5 h-3.5" /> QR Code
              </button>
              <button onClick={() => toggleTableStatus(table)} className={`px-3 py-2 rounded-lg text-xs font-bold ${table.is_active ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {table.is_active ? t.deactivate : t.activate}
              </button>
              <button onClick={() => setEditingTable({ ...table })} className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">
                <Edit className="w-4 h-4" />
              </button>
              <button onClick={() => deleteTable(table.id)} className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {tables.length === 0 && (
        <div className="text-center py-12 text-slate-400">No tables. Click "{t.addNewTable}" to add one.</div>
      )}

      {/* QR Code Modal */}
      {qrModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="qr-modal">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 relative">
            <button onClick={() => setQrModal(null)} className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600" data-testid="qr-modal-close">
              <X className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-1">Table {qrModal.table_number}</h3>
              <p className="text-sm text-slate-500 mb-5">Scan to open the ordering menu</p>
              <div className="bg-white border border-slate-100 rounded-xl p-4 inline-block mb-5">
                <img src={qrModal.qr_image} alt={`QR Code for Table ${qrModal.table_number}`} className="w-56 h-56" data-testid="qr-code-image" />
              </div>
              <p className="text-xs text-slate-400 mb-5 break-all">{qrModal.order_url}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => downloadQR(qrModal.table_number, qrModal.qr_image)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 transition-colors"
                  data-testid="qr-download-btn"
                >
                  <Download className="w-4 h-4" /> Download
                </button>
                <button
                  onClick={() => printQR(qrModal.table_number, qrModal.qr_image, qrModal.order_url)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 transition-colors"
                  data-testid="qr-print-btn"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Banner Ads Management
  const renderBanners = () => (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="admin-banners-section">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-900">Banner Ads</h2>
        <button onClick={() => openBannerEditor()} className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-colors" data-testid="add-banner-btn">
          <Plus className="w-5 h-5" /> Add Banner
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        Create promotional banners for partners (Grab, tour companies, local businesses). Add affiliate links and images. Banners show as a slider on the landing page and blog.
      </div>

      <div className="space-y-3">
        {bannerAds.map(banner => (
          <div key={banner.id} className={`bg-white rounded-xl border ${banner.is_active ? 'border-slate-200' : 'border-red-200 opacity-60'} overflow-hidden`} data-testid={`banner-${banner.id}`}>
            <div className="flex items-start gap-4 p-4">
              {/* Image thumbnail */}
              <div className="w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 relative group">
                {banner.image_url ? (
                  <img src={banner.image_url.startsWith('/') ? `${API_URL}${banner.image_url}` : banner.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300"><Image size={24} /></div>
                )}
                <button
                  onClick={() => {
                    const inp = document.createElement('input');
                    inp.type = 'file'; inp.accept = 'image/*';
                    inp.onchange = (e) => { if (e.target.files[0]) uploadBannerImage(banner.id, e.target.files[0]); };
                    inp.click();
                  }}
                  className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                >
                  <Upload size={16} />
                </button>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-slate-900 text-sm">{banner.title}</h3>
                {banner.link_url && (
                  <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 truncate block mt-1">{banner.link_url}</a>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${banner.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {banner.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-[10px] text-slate-400 uppercase font-medium">{banner.position}</span>
                  {banner.link_text && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">{banner.link_text}</span>}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => toggleBannerActive(banner)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${banner.is_active ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {banner.is_active ? 'Pause' : 'Enable'}
                </button>
                <button onClick={() => openBannerEditor(banner)} className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"><Edit size={14} /></button>
                <button onClick={() => deleteBannerAd(banner.id)} className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {bannerAds.length === 0 && (
        <div className="text-center py-12 text-slate-400">No banner ads yet. Click "Add Banner" to create your first promotional banner.</div>
      )}

      {/* Add/Edit Banner Modal */}
      {showAddBanner && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="add-banner-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">{editingBanner ? 'Edit Banner' : 'Create Banner Ad'}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1">Title / Company Name</label>
                <input type="text" value={newBanner.title} onChange={e => setNewBanner(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="e.g., Grab Vietnam" data-testid="banner-title-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1">Banner Image URL <span className="text-slate-400 font-normal">(paste link or upload after saving)</span></label>
                <input type="url" value={newBanner.image_url || ''} onChange={e => setNewBanner(p => ({ ...p, image_url: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="https://example.com/banner.jpg" data-testid="banner-image-url-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1">Affiliate / Promo Link</label>
                <input type="url" value={newBanner.link_url} onChange={e => setNewBanner(p => ({ ...p, link_url: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="https://grab.com/vn?ref=benzeatery" data-testid="banner-link-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1">Button Text</label>
                <input type="text" value={newBanner.link_text} onChange={e => setNewBanner(p => ({ ...p, link_text: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="e.g., Book Now, Visit, Learn More" data-testid="banner-linktext-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-600 block mb-1">Position</label>
                  <select value={newBanner.position} onChange={e => setNewBanner(p => ({ ...p, position: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="banner-position-select">
                    <option value="landing">Landing Page</option>
                    <option value="blog">Blog</option>
                    <option value="menu">Menu</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600 block mb-1">Sort Order</label>
                  <input type="number" value={newBanner.sort_order} onChange={e => setNewBanner(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="banner-sort-input" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="banner-active" checked={newBanner.is_active} onChange={e => setNewBanner(p => ({ ...p, is_active: e.target.checked }))} />
                <label htmlFor="banner-active" className="text-sm text-slate-600">Active immediately</label>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setShowAddBanner(false); setEditingBanner(null); }} className="flex-1 py-2 bg-slate-100 rounded-lg font-bold text-sm text-slate-600">Cancel</button>
              <button onClick={saveBanner} className="flex-1 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm" data-testid="confirm-save-banner">{editingBanner ? 'Update' : 'Create'}</button>
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">You can paste an image URL above OR upload an image file by hovering the thumbnail after saving.</p>
          </div>
        </div>
      )}
    </div>
  );

  // Render Staff Management
  const renderStaff = () => (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="admin-staff-section">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-900">Staff Accounts</h2>
        <button onClick={() => setShowAddStaff(true)} className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-indigo-700 transition-colors" data-testid="add-staff-btn">
          <Plus className="w-5 h-5" /> Add Staff
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        Staff members login at <strong>/staff</strong> with their username and password. They can manage orders, take new orders, and print invoices.
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {staffList.map(s => (
          <div key={s.id} className={`bg-white rounded-xl p-5 border ${s.is_active ? 'border-slate-200' : 'border-red-200 opacity-60'}`} data-testid={`staff-${s.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{s.display_name || s.username}</h3>
                <p className="text-sm text-slate-500">@{s.username}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${s.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {s.is_active !== false ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggleStaffActive(s.id)} className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold ${s.is_active !== false ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                {s.is_active !== false ? 'Deactivate' : 'Activate'}
              </button>
              <button onClick={() => resetStaffPassword(s.id)} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold hover:bg-blue-200">
                Reset PW
              </button>
              <button onClick={() => deleteStaffAccount(s.id)} className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {staffList.length === 0 && (
        <div className="text-center py-12 text-slate-400">No staff accounts. Click "Add Staff" to create one.</div>
      )}

      {/* Add Staff Modal */}
      {showAddStaff && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="add-staff-modal">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold mb-4">Create Staff Account</h3>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1">Username</label>
                <input
                  type="text"
                  value={newStaff.username}
                  onChange={e => setNewStaff(p => ({ ...p, username: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="e.g., waiter1"
                  data-testid="new-staff-username"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1">Display Name</label>
                <input
                  type="text"
                  value={newStaff.display_name}
                  onChange={e => setNewStaff(p => ({ ...p, display_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="e.g., John"
                  data-testid="new-staff-displayname"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 block mb-1">Password</label>
                <input
                  type="text"
                  value={newStaff.password}
                  onChange={e => setNewStaff(p => ({ ...p, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  placeholder="Min 4 characters"
                  data-testid="new-staff-password"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowAddStaff(false)} className="flex-1 py-2 bg-slate-100 rounded-lg font-bold text-sm text-slate-600">Cancel</button>
              <button onClick={createStaffAccount} className="flex-1 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm" data-testid="confirm-add-staff">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderBlog = () => (
    <div className="max-w-5xl mx-auto" data-testid="admin-blog-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-900">{t.blog}</h2>
        <button
          onClick={() => openBlogEditor()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-indigo-700 transition-colors"
          data-testid="add-blog-post-btn"
        >
          <Plus size={16} /> {t.addPost}
        </button>
      </div>

      {blogPosts.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <PenLine size={40} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">{t.noPosts}</p>
        </div>
      )}

      <div className="grid gap-4">
        {blogPosts.map(post => (
          <div key={post.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start gap-5" data-testid={`blog-post-${post.id}`}>
            {/* Cover image thumbnail */}
            <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-slate-100 relative group">
              {post.cover_image ? (
                <img
                  src={post.cover_image.startsWith('/') ? `${API_URL}${post.cover_image}` : post.cover_image}
                  alt="" className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-300">
                  <Image size={24} />
                </div>
              )}
              <button
                onClick={() => {
                  const inp = document.createElement('input');
                  inp.type = 'file';
                  inp.accept = 'image/*';
                  inp.onchange = (e) => {
                    if (e.target.files[0]) uploadBlogCoverImage(post.id, e.target.files[0]);
                  };
                  inp.click();
                }}
                className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                data-testid={`blog-upload-image-${post.id}`}
              >
                <Upload size={16} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${post.published ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {post.published ? t.published : t.draft}
                </span>
                {post.tags?.map(tag => (
                  <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-[10px] font-medium">{tag}</span>
                ))}
              </div>
              <h3 className="font-bold text-slate-900 text-base truncate">{post.title}</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{post.excerpt}</p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => toggleBlogPublish(post)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold ${post.published ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}
                data-testid={`blog-toggle-publish-${post.id}`}
              >
                {post.published ? t.unpublish : t.publish}
              </button>
              <button
                onClick={() => openBlogEditor(post)}
                className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                data-testid={`blog-edit-${post.id}`}
              >
                <Edit size={14} />
              </button>
              <button
                onClick={() => deleteBlogPost(post.id)}
                className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                data-testid={`blog-delete-${post.id}`}
              >
                <Trash2 size={14} />
              </button>
              <a
                href={`/blog/${post.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200"
                data-testid={`blog-view-${post.id}`}
              >
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render Screens
  const renderScreens = () => <AdminScreensTab t={t} />;

  // Generate QR Cards HTML (opens in new tab for printing).
  // Uses ONLY the tables selected from the real Tables DB — this prevents
  // generating QR codes for table numbers that don't exist in the system
  // (those QRs would be rejected by the order API anyway).
  const generateQRCardsHTML = () => {
    const activeTables = (tables || []).filter(t => t.is_active !== false);
    const selectedIds = qrCardForm.selectedTables || [];
    const chosen = activeTables
      .filter(t => selectedIds.includes(t.id))
      .map(t => String(t.number));
    const tableNumbers = chosen.length ? chosen : [];
    const name = qrCardForm.restaurantName || 'Benz Eatery';
    const tag = qrCardForm.tagline || 'Authentic Vietnamese Kitchen';
    const wifi = qrCardForm.wifiName || 'BenzGuest';
    const url = qrCardForm.orderUrl.replace(/^https?:\/\//, '') || 'benzeatery.com/order';
    const logo = qrCardLogo;

    const cardsHTML = tableNumbers.map(num => {
      const fullUrl = 'https://' + url + '?table=' + num;
      const logoHTML = logo ? `<div style="margin:0 auto 8px;width:48px;height:48px;border-radius:12px;overflow:hidden;border:1px solid rgba(255,255,255,0.3)"><img src="${logo}" style="width:100%;height:100%;object-fit:contain" /></div>` : '';
      return `<div class="card-col"><div class="card-tag">Table ${num}</div><div class="qr-card" id="card-t${num}">
        <div class="card-bg"><svg style="position:absolute;inset:0;width:100%;height:100%" viewBox="0 0 310 480" preserveAspectRatio="xMidYMid slice"><rect width="310" height="480" fill="#1a6b9e"/><rect width="310" height="260" fill="#1460a0"/><rect y="200" width="310" height="80" fill="#1e7bba"/><circle cx="20" cy="18" r="1" fill="#fff" opacity=".7"/><circle cx="55" cy="8" r=".8" fill="#fff" opacity=".6"/><circle cx="90" cy="22" r="1.2" fill="#fff" opacity=".8"/><circle cx="130" cy="12" r=".7" fill="#fff" opacity=".5"/><circle cx="160" cy="5" r="1" fill="#fff" opacity=".7"/><circle cx="200" cy="20" r=".9" fill="#fff" opacity=".6"/><circle cx="240" cy="10" r="1.1" fill="#fff" opacity=".75"/><circle cx="280" cy="16" r=".8" fill="#fff" opacity=".55"/><circle cx="155" cy="230" r="90" fill="#f08018" opacity=".18"/><circle cx="155" cy="230" r="60" fill="#f5a020" opacity=".35"/><circle cx="155" cy="230" r="38" fill="#f5c842" opacity=".65"/><circle cx="155" cy="230" r="24" fill="#ffd54f" opacity=".92"/><rect x="0" y="224" width="310" height="12" fill="#f08018" opacity=".5"/><path d="M0 236 Q78 222 155 232 Q232 242 310 228 L310 480 L0 480Z" fill="#0e6696"/><path d="M0 252 Q78 240 155 250 Q232 260 310 246 L310 480 L0 480Z" fill="#0c5580"/><path d="M0 272 Q78 262 155 270 Q232 278 310 264 L310 480 L0 480Z" fill="#0a3f62"/><path d="M0 300 Q78 292 155 298 Q232 304 310 292 L310 480 L0 480Z" fill="#082e4a"/><path d="M130 234 Q155 240 180 234" stroke="#f5c842" stroke-width="2" fill="none" opacity=".5"/><path d="M118 244 Q155 253 192 244" stroke="#f5c842" stroke-width="1.5" fill="none" opacity=".3"/><path d="M60 160 Q65 155 70 160" stroke="rgba(255,255,255,.35)" stroke-width="1.2" fill="none"/><path d="M240 148 Q246 143 252 148" stroke="rgba(255,255,255,.3)" stroke-width="1.2" fill="none"/><path d="M30 480 Q36 400 45 360 Q50 330 42 295" stroke="#0d2e14" stroke-width="7" stroke-linecap="round"/><path d="M42 295 Q12 272 2 248" stroke="#0d2e14" stroke-width="5" stroke-linecap="round"/><path d="M42 295 Q22 265 28 240" stroke="#0d2e14" stroke-width="4.5" stroke-linecap="round"/><path d="M42 295 Q46 260 62 248" stroke="#0d2e14" stroke-width="4.5" stroke-linecap="round"/><path d="M42 295 Q60 272 78 264" stroke="#0d2e14" stroke-width="4" stroke-linecap="round"/><ellipse cx="8" cy="250" rx="14" ry="6" fill="#1a5c24" transform="rotate(-25 8 250)"/><ellipse cx="28" cy="242" rx="15" ry="6" fill="#1e6b2a" transform="rotate(-12 28 242)"/><ellipse cx="62" cy="250" rx="15" ry="6" fill="#1a5c24" transform="rotate(18 62 250)"/><ellipse cx="78" cy="266" rx="13" ry="5.5" fill="#1e6b2a" transform="rotate(28 78 266)"/><path d="M280 480 Q274 400 265 360 Q260 330 268 295" stroke="#0d2e14" stroke-width="7" stroke-linecap="round"/><path d="M268 295 Q298 272 308 248" stroke="#0d2e14" stroke-width="5" stroke-linecap="round"/><path d="M268 295 Q288 265 282 240" stroke="#0d2e14" stroke-width="4.5" stroke-linecap="round"/><path d="M268 295 Q264 260 248 248" stroke="#0d2e14" stroke-width="4.5" stroke-linecap="round"/><path d="M268 295 Q250 272 232 264" stroke="#0d2e14" stroke-width="4" stroke-linecap="round"/><ellipse cx="302" cy="250" rx="14" ry="6" fill="#1a5c24" transform="rotate(25 302 250)"/><ellipse cx="282" cy="242" rx="15" ry="6" fill="#1e6b2a" transform="rotate(12 282 242)"/><ellipse cx="248" cy="250" rx="15" ry="6" fill="#1a5c24" transform="rotate(-18 248 250)"/><ellipse cx="232" cy="266" rx="13" ry="5.5" fill="#1e6b2a" transform="rotate(-28 232 266)"/></svg><div class="bg-overlay"></div></div>
        <div class="card-content">
          <div class="card-top">${logoHTML}<div class="brand-name">${name}</div><div class="brand-loc"><div class="loc-dot"></div><span>Phu Quoc &middot; Vietnam</span><div class="loc-dot"></div></div></div>
          <div class="card-mid"><div class="table-label">Table</div><div class="table-num">${num}</div><div class="scan-divider"><div class="scan-line"></div><div class="scan-text">Scan to order</div><div class="scan-line"></div></div><div class="qr-box"><canvas id="qr-card-t${num}" width="168" height="168"></canvas></div><div class="tagline">${tag}</div></div>
          <div class="card-foot"><div class="foot-item"><div class="foot-lbl">Wi-Fi</div><div class="foot-val">${wifi}</div></div><div class="foot-sep"></div><div class="foot-gems"><div class="fg sm"></div><div class="fg"></div><div class="fg sm"></div></div><div class="foot-sep"></div><div class="foot-item" style="text-align:right"><div class="foot-lbl">C&#7843;m &#417;n</div><div class="foot-val">Thank you</div></div></div>
        </div>
      </div></div>`;
    }).join('');

    const qrScript = tableNumbers.map((num, i) => `setTimeout(function(){makeQR('qr-card-t${num}','https://${url}?table=${num}');},${100 + i * 80})`).join(';');

    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Table QR Cards - ${name}</title><link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet"><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'DM Sans',sans-serif;background:#0a1a14;min-height:100vh;display:flex;flex-direction:column;align-items:center;padding:2.5rem 1.5rem 3rem;gap:2rem}.page-title{font-family:'Cormorant Garamond',serif;font-size:1.5rem;font-weight:400;font-style:italic;color:#e8f0eb;letter-spacing:.04em;display:flex;align-items:center;gap:12px}.page-title span{font-family:'DM Sans',sans-serif;font-style:normal;font-size:.7rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#4a7a60}.cards-grid{display:grid;grid-template-columns:repeat(auto-fill,310px);gap:2rem;justify-content:center;width:100%;max-width:900px}.card-col{display:flex;flex-direction:column;align-items:center;gap:8px}.card-tag{font-size:10px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#4a7a60}.qr-card{width:310px;border-radius:18px;overflow:hidden;position:relative;border:1px solid rgba(255,255,255,.12)}.card-bg{position:absolute;inset:0;z-index:0;overflow:hidden}.bg-overlay{position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.06) 0%,rgba(0,0,0,0) 38%,rgba(0,0,0,.5) 72%,rgba(0,0,0,.72) 100%)}.card-content{position:relative;z-index:2;display:flex;flex-direction:column;align-items:center}.card-top{padding:1.4rem 1.5rem .5rem;text-align:center;width:100%}.brand-name{font-family:'Cormorant Garamond',serif;font-size:1.75rem;font-weight:400;font-style:italic;color:#fff;letter-spacing:.08em;line-height:1;text-shadow:0 1px 10px rgba(0,0,0,.45)}.brand-loc{font-size:8.5px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;color:rgba(255,220,140,.8);margin-top:5px;display:flex;align-items:center;justify-content:center;gap:5px}.loc-dot{width:3px;height:3px;background:#f5c842;border-radius:50%}.card-mid{padding:.5rem 1.5rem 1.25rem;text-align:center;width:100%}.table-label{font-size:8.5px;font-weight:700;letter-spacing:.22em;text-transform:uppercase;color:rgba(255,220,140,.9);margin-bottom:1px}.table-num{font-family:'Cormorant Garamond',serif;font-size:4.2rem;font-weight:300;color:#fff;line-height:1;margin-bottom:.9rem;text-shadow:0 2px 14px rgba(0,0,0,.4)}.scan-divider{display:flex;align-items:center;gap:8px;justify-content:center;margin-bottom:.9rem}.scan-line{flex:1;height:1px;background:rgba(255,255,255,.2)}.scan-text{font-size:8.5px;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:rgba(255,255,255,.6);white-space:nowrap}.qr-box{background:rgba(255,255,255,.97);border-radius:12px;padding:10px;display:inline-block;margin-bottom:.9rem;border:2px solid rgba(255,255,255,.4)}.qr-box canvas{display:block;border-radius:4px}.tagline{font-family:'Cormorant Garamond',serif;font-style:italic;font-size:12.5px;color:rgba(255,220,140,.75);letter-spacing:.04em;margin-bottom:1.2rem}.card-foot{width:100%;background:rgba(0,0,0,.32);border-top:1px solid rgba(255,255,255,.1);padding:.85rem 1.5rem;display:flex;align-items:center;justify-content:space-between}.foot-item{text-align:center}.foot-lbl{font-size:7.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:rgba(255,220,140,.5);margin-bottom:2px}.foot-val{font-size:11.5px;color:rgba(255,255,255,.85);letter-spacing:.02em}.foot-sep{width:1px;height:26px;background:rgba(255,255,255,.12)}.foot-gems{display:flex;gap:4px;align-items:center;justify-content:center}.fg{width:5px;height:5px;background:rgba(245,200,66,.5);transform:rotate(45deg)}.fg.sm{width:3px;height:3px;background:rgba(245,200,66,.25)}.btn-row{display:flex;gap:12px;flex-wrap:wrap;justify-content:center}.abtn{display:flex;align-items:center;gap:8px;padding:13px 28px;font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;cursor:pointer;font-family:'DM Sans',sans-serif;transition:all .15s;border:none;border-radius:3px}.abtn-pr{background:transparent;color:#7ab87a;border:1.5px solid rgba(122,184,122,.3)}.abtn-pr:hover{background:rgba(122,184,122,.06);border-color:#7ab87a}.abtn svg{width:14px;height:14px}@media print{body{background:#fff!important;padding:0;margin:0;display:block}.page-title,.btn-row,.card-tag{display:none!important}.cards-grid{display:grid;grid-template-columns:repeat(2,105mm);gap:6mm;padding:10mm;page-break-inside:avoid}.card-col{break-inside:avoid}.qr-card{width:105mm;border-radius:4mm;border:0.5pt solid rgba(255,255,255,.2);break-inside:avoid;-webkit-print-color-adjust:exact;print-color-adjust:exact}@page{size:A4 portrait;margin:8mm}}</style></head><body><div class="page-title">Table QR Cards<span>${name} &middot; Phu Quoc</span></div><div class="cards-grid">${cardsHTML}</div><div class="btn-row"><button class="abtn abtn-pr" onclick="window.print()"><svg viewBox="0 0 14 14" fill="none"><rect x="2" y="5" width="10" height="7" rx="1" stroke="#7ab87a" stroke-width="1.4"/><path d="M4 5V3h6v2M4 9h6" stroke="#7ab87a" stroke-width="1.4" stroke-linecap="round"/></svg>Print all cards</button></div><script src="https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js"><\/script><script>function makeQR(id,url){var c=document.getElementById(id);if(!c)return;var f=c.parentElement;f.querySelectorAll('img').forEach(function(x){x.remove()});var ctx=c.getContext('2d');ctx.clearRect(0,0,168,168);try{new QRCode(f,{text:url,width:168,height:168,colorDark:'#0a2010',colorLight:'#ffffff',correctLevel:QRCode.CorrectLevel.M});setTimeout(function(){var imgs=f.querySelectorAll('img');if(imgs.length){var img=imgs[imgs.length-1];function draw(){ctx.drawImage(img,0,0,168,168);img.remove();}if(img.complete)draw();else img.onload=draw;}},160);}catch(e){}}${qrScript}<\/script></body></html>`;
  };

  // Render Settings
  const renderSettings = () => {
    if (!settings) return <div className="text-slate-400">Loading settings...</div>;

    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex gap-8">
          {/* Settings Sidebar */}
          <div className="w-56 flex-shrink-0 flex flex-col gap-1">
            {settingsOptions.map(opt => {
              const IconComponent = opt.icon;
              return (
                <button
                  key={opt.id}
                  onClick={() => setSettingsTab(opt.id)}
                  className={`flex items-center gap-3 px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors ${
                    settingsTab === opt.id ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'text-slate-500 hover:bg-slate-100'
                  }`}
                >
                  <IconComponent size={16} />
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Settings Content */}
          <div className="flex-1 space-y-6">
            {/* General Settings */}
            {settingsTab === 'general' && (
              <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
                <h3 className="font-bold text-lg mb-6">{t.general}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Restaurant Name</label>
                    <input type="text" value={settings.restaurant_name || ''} onChange={(e) => setSettings({...settings, restaurant_name: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl text-sm" placeholder="Benz Eatery" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Address</label>
                    <input type="text" value={settings.address || ''} onChange={(e) => setSettings({...settings, address: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl text-sm" placeholder="123 Main St" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Phone Number</label>
                    <input type="text" value={settings.phone || ''} onChange={(e) => setSettings({...settings, phone: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl text-sm" placeholder="+84 xxx xxx xxx" />
                  </div>
                </div>
              </div>
            )}

            {/* Printers Settings */}
            {settingsTab === 'printers' && (
              <div className="space-y-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-6 text-white shadow-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-xl flex items-center gap-2">
                        {adminLang === 'en' ? 'Web Printing' : 'In qua Trinh duyet'}
                      </h3>
                      <p className="text-amber-100 text-sm mt-2 max-w-lg">
                        {adminLang === 'en' 
                          ? 'Printing uses your browser\'s built-in print dialog. Make sure your printer is connected to your computer.'
                          : 'In sử dụng hộp thoại in của trình duyệt. Đảm bảo máy in đã kết nối với máy tính.'}
                      </p>
                    </div>
                    <div className="text-6xl opacity-30"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z"/></svg></div>
                  </div>
                </div>

                {/* How It Works */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                  <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <Printer size={20} className="text-amber-600" />
                    {adminLang === 'en' ? 'How Printing Works' : 'Cách In'}
                  </h4>
                  
                  <div className="space-y-4">
                    {/* Order Tokens */}
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-amber-500 text-white rounded-lg flex items-center justify-center">
                          <ChefHat size={20} />
                        </div>
                        <div>
                          <h5 className="font-bold text-slate-900">{adminLang === 'en' ? 'Order Tokens (Kitchen/Bar)' : 'Phiếu đơn (Bếp/Bar)'}</h5>
                          <p className="text-sm text-slate-600">{adminLang === 'en' ? 'Print when you click "Send All" in POS' : 'In khi bấm "Gửi tất cả" trong POS'}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Final Bills */}
                    <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-emerald-500 text-white rounded-lg flex items-center justify-center">
                          <Receipt size={20} />
                        </div>
                        <div>
                          <h5 className="font-bold text-slate-900">{adminLang === 'en' ? 'Final Bills' : 'Hóa đơn'}</h5>
                          <p className="text-sm text-slate-600">{adminLang === 'en' ? 'Print when you click "Print Bill" or "Close Table"' : 'In khi bấm "In hóa đơn" hoặc "Đóng bàn"'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tips */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h4 className="font-bold text-blue-800 mb-2">{adminLang === 'en' ? 'Tips' : 'Meo'}</h4>
                  <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li>{adminLang === 'en' ? 'Set your thermal printer as default in Windows/Mac for faster printing' : 'Đặt máy in nhiệt làm mặc định trong Windows/Mac để in nhanh hơn'}</li>
                    <li>{adminLang === 'en' ? 'Use Chrome or Edge browser for best printing support' : 'Dùng Chrome hoặc Edge để in tốt nhất'}</li>
                    <li>{adminLang === 'en' ? 'Paper width is optimized for 80mm thermal paper' : 'Khổ giấy tối ưu cho giấy nhiệt 80mm'}</li>
                  </ul>
                </div>

                {/* Download Offline System */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-xl flex items-center gap-2">
                        {adminLang === 'en' ? 'Offline Windows System' : 'He thong Windows Offline'}
                      </h3>
                      <p className="text-indigo-100 text-sm mt-2 max-w-lg">
                        {adminLang === 'en' 
                          ? 'Download complete POS system to run locally on your Windows PC. Includes direct USB printer support - no browser printing dialog!'
                          : 'Tải hệ thống POS đầy đủ để chạy trên máy Windows. Hỗ trợ in USB trực tiếp - không cần hộp thoại in của trình duyệt!'}
                      </p>
                    </div>
                    <Download size={32} className="text-indigo-200" />
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <a 
                      href={`${API_URL}/api/download/offline-system`} 
                      className="px-6 py-3 bg-white text-indigo-600 hover:bg-indigo-50 rounded-xl text-sm font-bold flex items-center gap-2 transition-all"
                    >
                      <Download size={18} />
                      {adminLang === 'en' ? 'Download Offline System (ZIP)' : 'Tải hệ thống Offline (ZIP)'}
                    </a>
                  </div>
                  <p className="text-indigo-200 text-xs mt-3">
                    {adminLang === 'en' 
                      ? 'Includes: Backend + Frontend + Direct USB Printing + Local Database' 
                      : 'Bao gồm: Backend + Frontend + In USB trực tiếp + Database cục bộ'}
                  </p>
                </div>
              </div>
            )}

            {/* Invoice Settings */}
            {settingsTab === 'invoice' && (
              <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
                <h3 className="font-bold text-lg mb-6">{adminLang === 'en' ? 'Invoice & Receipt Builder' : 'Tùy chỉnh Hóa đơn'}</h3>
                <div className="grid grid-cols-2 gap-8">
                  <div className="space-y-5">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">QR Code URL</label>
                      <input type="text" value={settings.qr_code_url || ''} onChange={(e) => setSettings({...settings, qr_code_url: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl text-sm" placeholder="https://..." />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Footer Message</label>
                      <textarea value={settings.footer_text || ''} onChange={(e) => setSettings({...settings, footer_text: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl text-sm h-20 resize-none" placeholder="Cảm ơn quý khách! Hẹn gặp lại." />
                    </div>
                    
                    {/* Credit Card Fee */}
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-slate-900">Credit Card Fee</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" checked={settings.enable_credit_card_fee !== false} onChange={(e) => setSettings({...settings, enable_credit_card_fee: e.target.checked})} className="sr-only peer" />
                          <div className="w-10 h-5 bg-slate-300 rounded-full peer peer-checked:bg-indigo-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                        </label>
                      </div>
                      {settings.enable_credit_card_fee !== false && (
                        <div className="flex items-center gap-2">
                          <input type="number" min="0" max="100" step="0.5" value={settings.credit_card_fee_percent || 3} onChange={(e) => setSettings({...settings, credit_card_fee_percent: parseFloat(e.target.value) || 0})} className="w-20 p-2 border border-slate-200 rounded-lg text-sm text-center" />
                          <span className="text-lg font-bold text-slate-400">%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div className="bg-slate-800 rounded-2xl p-6 text-white flex flex-col items-center">
                    <p className="text-[10px] text-slate-400 font-bold uppercase mb-4 self-start">Template Preview</p>
                    <div className="bg-white text-black p-4 w-44 shadow-2xl text-[9px] font-mono leading-tight space-y-1">
                      <div className="text-center border-b border-dashed border-black pb-2 mb-2">
                        <p className="font-black text-xs">{settings.restaurant_name || 'BENZ®'}</p>
                        <p>{settings.address || 'Store #01'}</p>
                      </div>
                      <div className="flex justify-between font-bold">
                        <span>2x Pho Special</span>
                        <span>$14.00</span>
                      </div>
                      <div className="flex justify-between">
                        <span>1x Green Tea</span>
                        <span>$2.50</span>
                      </div>
                      <div className="border-t border-black pt-2 flex justify-between font-bold text-xs">
                        <span>TOTAL</span>
                        <span>$16.50</span>
                      </div>
                      <div className="text-center pt-4 italic">
                        {settings.footer_text || (adminLang === 'en' ? 'Thank you!' : 'Cảm ơn quý khách!')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Settings */}
            {settingsTab === 'notifications' && (
              <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
                <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
                  <Volume2 className="w-5 h-5 text-amber-500" />
                  Order Notification Sound
                </h3>
                
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-bold text-slate-900">Enable Sound</label>
                        <p className="text-xs text-slate-500 mt-1">Play sound when new orders arrive</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={settings.notification_sound_enabled !== false} onChange={(e) => setSettings({...settings, notification_sound_enabled: e.target.checked})} className="sr-only peer" />
                        <div className="w-10 h-5 bg-slate-300 rounded-full peer peer-checked:bg-amber-500 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                      </label>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl">
                    <label className="text-sm font-bold text-slate-900 block mb-3">Custom Sound File</label>
                    
                    {settings.notification_sound_url ? (
                      <div className="flex items-center gap-3 mb-3 p-3 bg-white rounded-lg border border-slate-200">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Volume2 className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">Custom Sound Uploaded</p>
                          <p className="text-xs text-slate-500">{settings.notification_sound_url.split('/').pop()}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => { setTestingSound(true); const audio = new Audio(`${API_URL}${settings.notification_sound_url}`); audio.play().finally(() => setTestingSound(false)); }} disabled={testingSound} className="p-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200">
                            <Play className="w-4 h-4" />
                          </button>
                          <button onClick={async () => { if (window.confirm('Remove custom sound?')) { try { await axios.delete(`${API_URL}/api/settings/notification-sound`); setSettings({...settings, notification_sound_url: ''}); } catch (err) { alert('Failed'); } } }} className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 mb-3 p-3 bg-white rounded-lg border border-dashed border-slate-300">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Volume2 className="w-5 h-5 text-slate-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-600">Using Default Sound</p>
                          <p className="text-xs text-slate-400">Upload a custom sound below</p>
                        </div>
                      </div>
                    )}

                    <input type="file" ref={fileInputRef} accept="audio/mpeg,audio/wav,audio/ogg,.mp3,.wav,.ogg" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { alert('File too large. Max: 5MB'); return; }
                      setSoundUploading(true);
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await axios.post(`${API_URL}/api/settings/notification-sound`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setSettings({...settings, notification_sound_url: response.data.sound_url});
                        alert('Sound uploaded!');
                      } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
                      finally { setSoundUploading(false); e.target.value = ''; }
                    }} />
                    
                    <button onClick={() => fileInputRef.current?.click()} disabled={soundUploading} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 disabled:opacity-50">
                      {soundUploading ? (<><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Uploading...</>) : (<><Upload className="w-5 h-5" />Upload Sound File</>)}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Features Settings */}
            {settingsTab === 'features' && (
              <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
                <h3 className="font-bold text-lg mb-6 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-500" />
                  Feature Toggles
                </h3>
                
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-bold text-slate-900">Preparation Suggestions</label>
                        <p className="text-xs text-slate-500 mt-1">Show customization options when adding items</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={settings.enable_prep_suggestions !== false} onChange={(e) => setSettings({...settings, enable_prep_suggestions: e.target.checked})} className="sr-only peer" />
                        <div className="w-10 h-5 bg-slate-300 rounded-full peer peer-checked:bg-purple-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Landing Page Section Toggles — admin controls which sections
                appear on the public landing page. Stored in settings.landing_sections. */}
            {settingsTab === 'landing' && (
              <div className="space-y-6" data-testid="landing-settings">
                <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
                  <h3 className="font-bold text-lg mb-2">
                    {adminLang === 'en' ? 'Landing Page Sections' : 'Các phần trang chủ'}
                  </h3>
                  <p className="text-sm text-slate-500 mb-6 font-medium">
                    {adminLang === 'en'
                      ? 'Turn sections on or off on the public landing page. Changes apply immediately after Save.'
                      : 'Bật/tắt các phần trên trang chủ công khai. Thay đổi áp dụng sau khi Lưu.'}
                  </p>

                  <div className="space-y-3" data-testid="landing-sections-list">
                    {[
                      { key: 'top_dishes', en: 'Top Dishes Showcase', vi: 'Món ăn nổi bật', desc_en: 'Promoted / featured menu items at the top', desc_vi: 'Món ăn được quảng bá ở trên cùng' },
                      { key: 'blog', en: 'Blog & Food Stories', vi: 'Blog & Câu chuyện ẩm thực', desc_en: 'Latest stories filter + grid of articles', desc_vi: 'Bộ lọc và lưới bài viết mới nhất' },
                      { key: 'spotlight', en: 'Phu Quoc Spotlight', vi: 'Điểm nổi bật Phú Quốc', desc_en: 'Island highlight block with stats & CTA', desc_vi: 'Khối giới thiệu đảo với thống kê & CTA' },
                      { key: 'travel_tips', en: 'Travel Tips', vi: 'Mẹo du lịch', desc_en: 'Visa / currency / weather card', desc_vi: 'Thẻ visa / tiền tệ / thời tiết' },
                      { key: 'popular', en: 'Popular Stories', vi: 'Bài viết phổ biến', desc_en: 'Sidebar list of popular blog posts', desc_vi: 'Danh sách bài viết phổ biến' },
                      { key: 'newsletter', en: 'Newsletter Signup', vi: 'Đăng ký bản tin', desc_en: 'Email subscription CTA at the bottom', desc_vi: 'CTA đăng ký email ở cuối trang' },
                    ].map(row => {
                      const current = settings.landing_sections?.[row.key] !== false;
                      return (
                        <div
                          key={row.key}
                          className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-xl"
                          data-testid={`landing-section-row-${row.key}`}
                        >
                          <div>
                            <div className="font-bold text-sm text-slate-900">
                              {adminLang === 'en' ? row.en : row.vi}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {adminLang === 'en' ? row.desc_en : row.desc_vi}
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4">
                            <input
                              type="checkbox"
                              checked={current}
                              onChange={(e) => setSettings({
                                ...settings,
                                landing_sections: {
                                  ...(settings.landing_sections || {}),
                                  [row.key]: e.target.checked,
                                },
                              })}
                              className="sr-only peer"
                              data-testid={`landing-toggle-${row.key}`}
                            />
                            <div className="w-11 h-6 bg-slate-300 rounded-full peer-checked:bg-emerald-500 transition-colors peer-focus:ring-2 peer-focus:ring-emerald-300"></div>
                            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Branding Settings */}
            {settingsTab === 'branding' && (
              <div className="space-y-6" data-testid="branding-settings">
                <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
                  <h3 className="font-bold text-lg mb-2">{t.branding}</h3>
                  <p className="text-sm text-slate-500 mb-6 font-medium">{t.brandingDesc}</p>

                  {/* Logo Upload */}
                  <div className="p-6 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                    <div className="flex items-center gap-6">
                      <div className="w-24 h-24 bg-white rounded-2xl border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {settings?.restaurant_logo ? (
                          <img src={settings.restaurant_logo.startsWith('/') ? `${API_URL}${settings.restaurant_logo}` : settings.restaurant_logo} alt="Logo" className="w-full h-full object-contain p-1" data-testid="branding-logo-preview" />
                        ) : (
                          <Upload className="w-8 h-8 text-slate-300" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-sm mb-1">{t.restaurantLogo}</h4>
                        <p className="text-xs text-slate-400 mb-3">{t.logoUsed}</p>
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/png,image/jpeg,image/svg+xml,image/webp"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append('file', file);
                            try {
                              const res = await axios.post(`${API_URL}/api/settings/logo`, formData);
                              await loadSettings();
                              alert(t.logoUploaded);
                            } catch (err) {
                              alert('Upload failed: ' + (err.response?.data?.detail || err.message));
                            }
                          }}
                          data-testid="branding-logo-input"
                        />
                        <button onClick={() => document.getElementById('logo-upload').click()} className="bg-indigo-600 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-indigo-700" data-testid="branding-upload-btn">
                          {settings?.restaurant_logo ? t.changeLogo : t.uploadLogo}
                        </button>
                        {settings?.restaurant_logo && (
                          <button onClick={async () => { await settingsAPI.update({ restaurant_logo: '' }); await loadSettings(); }} className="ml-2 text-xs font-bold text-red-500 hover:underline" data-testid="branding-remove-logo">
                            {t.removeLogo}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Size Recommendations */}
                  <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-5">
                    <h4 className="font-bold text-sm text-blue-900 mb-3">{t.logoSizeRec}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <span className="text-xs font-bold text-blue-600 uppercase">{t.optimalSize}</span>
                        <p className="font-bold text-blue-900 mt-1">512 x 512 px</p>
                        <p className="text-xs text-blue-600">{t.squareFormat}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <span className="text-xs font-bold text-blue-600 uppercase">{t.minSize}</span>
                        <p className="font-bold text-blue-900 mt-1">128 x 128 px</p>
                        <p className="text-xs text-blue-600">{t.tooSmall}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <span className="text-xs font-bold text-blue-600 uppercase">{t.fileFormat}</span>
                        <p className="font-bold text-blue-900 mt-1">PNG or SVG</p>
                        <p className="text-xs text-blue-600">{t.transparentBg}</p>
                      </div>
                    </div>
                    <p className="text-xs text-blue-500 mt-3">{t.maxFileSize}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Finance */}
            {settingsTab === 'finance' && (
              <div className="space-y-6" data-testid="finance-settings">
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h3 className="font-bold text-lg flex items-center gap-2 mb-2">
                    <Wallet size={20} className="text-amber-500" />
                    {t.vietqrTitle}
                  </h3>
                  <p className="text-sm text-slate-500 mb-6">{t.vietqrDesc}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">{t.bank}</label>
                      <select
                        value={financeForm.bank_code}
                        onChange={(e) => { const bank = banksList.find(b => b.code === e.target.value); setFinanceForm(prev => ({ ...prev, bank_code: e.target.value, bank_name: bank?.name || '' })); }}
                        className="w-full p-3 border border-slate-200 rounded-xl text-sm"
                        data-testid="finance-bank-select"
                      >
                        <option value="">{t.selectBank}</option>
                        {banksList.map(bank => <option key={bank.code} value={bank.code}>{bank.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">{t.accountNumber}</label>
                      <input type="text" value={financeForm.bank_account_number} onChange={(e) => setFinanceForm(prev => ({ ...prev, bank_account_number: e.target.value.replace(/\D/g, '') }))} placeholder="1234567890" className="w-full p-3 border border-slate-200 rounded-xl text-sm" data-testid="finance-account-number" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">{t.accountHolder}</label>
                      <input type="text" value={financeForm.bank_account_holder} onChange={(e) => setFinanceForm(prev => ({ ...prev, bank_account_holder: e.target.value.toUpperCase() }))} placeholder="NGUYEN VAN A" className="w-full p-3 border border-slate-200 rounded-xl text-sm" data-testid="finance-account-holder" />
                    </div>
                  </div>
                  <button onClick={saveFinance} disabled={financeSaving || !financeForm.bank_code || !financeForm.bank_account_number || !financeForm.bank_account_holder} className="mt-4 bg-amber-500 text-white font-bold px-8 py-3 rounded-xl hover:bg-amber-600 disabled:opacity-50" data-testid="finance-save-btn">
                    {financeSaving ? t.saving : t.saveBankDetails}
                  </button>
                </div>
                {/* Test QR */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="finance-test-qr">
                  <h3 className="font-bold text-sm mb-4">{t.testQr}</h3>
                  <div className="flex items-end gap-3 mb-4">
                    <div className="flex-1">
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">{t.amount}</label>
                      <input type="number" value={financeTestAmount} onChange={(e) => setFinanceTestAmount(e.target.value)} className="w-full p-3 border border-slate-200 rounded-xl text-sm" data-testid="finance-test-amount" />
                    </div>
                    <button onClick={() => { if (financeForm.bank_code && financeForm.bank_account_number) { setFinancePreviewQR(`https://img.vietqr.io/image/${financeForm.bank_code}-${financeForm.bank_account_number}-compact2.png?amount=${financeTestAmount}&addInfo=${encodeURIComponent('Benz Eatery Test')}&accountName=${encodeURIComponent(financeForm.bank_account_holder || '')}`); } else { alert(t.saveBankFirst); } }} className="bg-indigo-600 text-white font-bold px-6 py-3 rounded-xl hover:bg-indigo-700 text-sm" data-testid="finance-generate-test">
                      {t.generateTestQr}
                    </button>
                  </div>
                  {financePreviewQR && (
                    <div className="text-center p-6 bg-slate-50 rounded-xl">
                      <img src={financePreviewQR} alt="VietQR Test" className="mx-auto max-w-[260px] rounded-xl" onError={(e) => { e.target.style.display = 'none'; }} />
                      <p className="text-xs text-slate-400 mt-3">{financeForm.bank_name} - {financeForm.bank_account_number} - {financeForm.bank_account_holder}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* QR Cards */}
            {settingsTab === 'qrcards' && (
              <div className="space-y-6" data-testid="qrcards-settings">
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h3 className="font-bold text-lg flex items-center gap-2 mb-2">
                    <Grid3X3 size={20} className="text-indigo-500" />
                    {t.qrCardTitle}
                  </h3>
                  <p className="text-sm text-slate-500 mb-6">{t.qrCardDesc}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">{t.restaurantName}</label>
                      <input type="text" value={qrCardForm.restaurantName} onChange={(e) => setQrCardForm(prev => ({ ...prev, restaurantName: e.target.value }))} className="w-full p-3 border border-slate-200 rounded-xl text-sm" data-testid="qr-restaurant-name" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">{t.tagline}</label>
                      <input type="text" value={qrCardForm.tagline} onChange={(e) => setQrCardForm(prev => ({ ...prev, tagline: e.target.value }))} className="w-full p-3 border border-slate-200 rounded-xl text-sm" data-testid="qr-tagline" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">{t.tablesRange}</label>
                      {tables.length === 0 ? (
                        <div className="w-full p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800" data-testid="qr-no-tables-msg">
                          {adminLang === 'en'
                            ? 'No tables configured yet. Add tables in the Tables tab to generate QR cards.'
                            : 'Chưa có bàn nào. Vui lòng thêm bàn trong tab Bàn để tạo thẻ QR.'}
                        </div>
                      ) : (
                        <div className="border border-slate-200 rounded-xl p-3 bg-slate-50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-slate-600">
                              {qrCardForm.selectedTables.length} / {tables.filter(t => t.is_active !== false).length} {adminLang === 'en' ? 'selected' : 'đã chọn'}
                            </span>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setQrCardForm(prev => ({ ...prev, selectedTables: tables.filter(tbl => tbl.is_active !== false).map(tbl => tbl.id) }))}
                                className="text-xs font-bold text-indigo-600 hover:underline"
                                data-testid="qr-select-all-tables"
                              >
                                {adminLang === 'en' ? 'Select all' : 'Chọn tất cả'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setQrCardForm(prev => ({ ...prev, selectedTables: [] }))}
                                className="text-xs font-bold text-slate-500 hover:underline"
                                data-testid="qr-clear-tables"
                              >
                                {adminLang === 'en' ? 'Clear' : 'Xóa'}
                              </button>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto" data-testid="qr-tables-picker">
                            {tables.filter(tbl => tbl.is_active !== false).map(tbl => {
                              const checked = qrCardForm.selectedTables.includes(tbl.id);
                              return (
                                <label
                                  key={tbl.id}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer border transition-all ${checked ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-400'}`}
                                  data-testid={`qr-table-chip-${tbl.number}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => setQrCardForm(prev => ({
                                      ...prev,
                                      selectedTables: checked
                                        ? prev.selectedTables.filter(id => id !== tbl.id)
                                        : [...prev.selectedTables, tbl.id],
                                    }))}
                                    className="hidden"
                                  />
                                  <span className="font-bold">{adminLang === 'en' ? 'Table' : 'Bàn'} {tbl.number}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">{t.wifiName}</label>
                      <input type="text" value={qrCardForm.wifiName} onChange={(e) => setQrCardForm(prev => ({ ...prev, wifiName: e.target.value }))} className="w-full p-3 border border-slate-200 rounded-xl text-sm" data-testid="qr-wifi" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">{t.orderUrl}</label>
                      <input type="text" value={qrCardForm.orderUrl} onChange={(e) => setQrCardForm(prev => ({ ...prev, orderUrl: e.target.value }))} className="w-full p-3 border border-slate-200 rounded-xl text-sm" data-testid="qr-order-url" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-bold text-slate-400 uppercase mb-2">{t.logo}</label>
                      <div className="flex items-center gap-4">
                        {qrCardLogo && <img src={qrCardLogo} alt="Logo" className="w-12 h-12 object-contain rounded-lg border border-slate-200" />}
                        <input type="file" ref={qrCardLogoRef} accept="image/*" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) { const reader = new FileReader(); reader.onload = (ev) => setQrCardLogo(ev.target.result); reader.readAsDataURL(file); } }} />
                        <button onClick={() => qrCardLogoRef.current?.click()} className="text-sm font-bold text-indigo-600 hover:underline">{qrCardLogo ? t.changeLogo : t.uploadLogo}</button>
                        {qrCardLogo && <button onClick={() => setQrCardLogo('')} className="text-sm font-bold text-red-500 hover:underline">{t.removeLogo}</button>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        if (!qrCardForm.selectedTables.length) {
                          alert(adminLang === 'en'
                            ? 'Please select at least one table to generate QR cards.'
                            : 'Vui lòng chọn ít nhất một bàn để tạo thẻ QR.');
                          return;
                        }
                        const w = window.open('', '_blank');
                        if (!w) return;
                        w.document.write(generateQRCardsHTML());
                        w.document.close();
                      }}
                      disabled={!qrCardForm.selectedTables.length}
                      className={`font-bold px-8 py-3 rounded-xl text-sm flex items-center gap-2 ${qrCardForm.selectedTables.length ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                      data-testid="qr-preview-btn"
                    >
                      <Eye size={16} /> {t.previewPrint}
                    </button>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
                  <strong>Tip:</strong> {t.qrTip}
                </div>
              </div>
            )}

            {/* Reviews Settings */}
            {settingsTab === 'reviews' && (
              <div className="space-y-6" data-testid="reviews-settings">
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h3 className="font-bold text-lg flex items-center gap-2 mb-2">
                    <Star size={20} className="text-amber-500" />
                    {t.reviewsTitle}
                  </h3>
                  <p className="text-sm text-slate-500 mb-6">{t.reviewsDesc}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Google */}
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                      <div className="flex items-center gap-2 mb-4">
                        <svg viewBox="0 0 24 24" width="20" height="20"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                        <span className="font-bold text-sm">{t.googleReviews}</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-bold text-slate-400 uppercase mb-1">{t.rating}</label>
                          <input type="number" step="0.1" min="1" max="5" value={settings.google_rating || ''} onChange={(e) => setSettings({...settings, google_rating: parseFloat(e.target.value) || 0})} className="w-full p-2.5 border border-slate-200 rounded-lg text-sm" data-testid="reviews-google-rating" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-400 uppercase mb-1">{t.reviewCount}</label>
                          <input type="number" value={settings.google_review_count || ''} onChange={(e) => setSettings({...settings, google_review_count: parseInt(e.target.value) || 0})} className="w-full p-2.5 border border-slate-200 rounded-lg text-sm" data-testid="reviews-google-count" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-400 uppercase mb-1">{t.reviewUrl}</label>
                          <input type="text" value={settings.google_review_url || ''} onChange={(e) => setSettings({...settings, google_review_url: e.target.value})} className="w-full p-2.5 border border-slate-200 rounded-lg text-sm" data-testid="reviews-google-url" />
                        </div>
                      </div>
                    </div>
                    {/* TripAdvisor */}
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                      <div className="flex items-center gap-2 mb-4">
                        <svg viewBox="0 0 24 24" width="20" height="20"><circle cx="12" cy="12" r="11" fill="#00AF87"/><path d="M12 6.5c-2.5 0-4.7 1.3-6 3.2h1.8a4.3 4.3 0 018.4 0h1.8c-1.3-1.9-3.5-3.2-6-3.2z" fill="white"/><circle cx="8.5" cy="13" r="2.8" fill="none" stroke="white" strokeWidth="1.3"/><circle cx="8.5" cy="13" r="1" fill="white"/><circle cx="15.5" cy="13" r="2.8" fill="none" stroke="white" strokeWidth="1.3"/><circle cx="15.5" cy="13" r="1" fill="white"/></svg>
                        <span className="font-bold text-sm">{t.tripadvisorReviews}</span>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-bold text-slate-400 uppercase mb-1">{t.rating}</label>
                          <input type="number" step="0.1" min="1" max="5" value={settings.tripadvisor_rating || ''} onChange={(e) => setSettings({...settings, tripadvisor_rating: parseFloat(e.target.value) || 0})} className="w-full p-2.5 border border-slate-200 rounded-lg text-sm" data-testid="reviews-ta-rating" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-400 uppercase mb-1">{t.reviewCount}</label>
                          <input type="number" value={settings.tripadvisor_review_count || ''} onChange={(e) => setSettings({...settings, tripadvisor_review_count: parseInt(e.target.value) || 0})} className="w-full p-2.5 border border-slate-200 rounded-lg text-sm" data-testid="reviews-ta-count" />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-400 uppercase mb-1">{t.reviewUrl}</label>
                          <input type="text" value={settings.tripadvisor_review_url || ''} onChange={(e) => setSettings({...settings, tripadvisor_review_url: e.target.value})} className="w-full p-2.5 border border-slate-200 rounded-lg text-sm" data-testid="reviews-ta-url" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Newsletter */}
            {settingsTab === 'newsletter' && (
              <div className="space-y-6" data-testid="newsletter-settings">
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h3 className="font-bold text-lg flex items-center gap-2 mb-2">
                    <Mail size={20} className="text-indigo-500" />
                    {t.newsletterTitle?.split(' &')[0] || 'Newsletter'}
                  </h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-sm">{t.subscriberCount}</h4>
                      <p className="text-3xl font-black text-indigo-600 mt-1">{subscriberCount}</p>
                    </div>
                    <Mail size={40} className="text-slate-200" />
                  </div>
                </div>
              </div>
            )}

            {/* Maintenance Mode */}
            {settingsTab === 'maintenance' && (
              <div className="space-y-6">
                <div className={`border rounded-2xl p-8 shadow-sm ${settings.maintenance_mode ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h3 className="font-bold text-lg flex items-center gap-2">
                        <ShieldAlert size={20} className={settings.maintenance_mode ? 'text-red-500' : 'text-slate-400'} />
                        Maintenance Mode
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">When enabled, all public pages show a maintenance message. Admin, staff, service admin, and partner portals remain accessible.</p>
                    </div>
                    <button
                      onClick={() => setSettings({...settings, maintenance_mode: !settings.maintenance_mode})}
                      className={`relative w-14 h-7 rounded-full transition-colors ${settings.maintenance_mode ? 'bg-red-500' : 'bg-slate-300'}`}
                      data-testid="maintenance-toggle"
                    >
                      <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.maintenance_mode ? 'translate-x-7' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {settings.maintenance_mode && (
                    <div className="bg-red-100 border border-red-300 rounded-xl p-4 mb-6">
                      <div className="flex items-center gap-2 text-red-700 font-bold text-sm">
                        <ShieldAlert size={16} />
                        MAINTENANCE MODE IS ACTIVE
                      </div>
                      <p className="text-red-600 text-xs mt-1">Public visitors will see the maintenance page instead of your website.</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Maintenance Message</label>
                    <textarea
                      value={settings.maintenance_message || ''}
                      onChange={(e) => setSettings({...settings, maintenance_message: e.target.value})}
                      className="w-full p-3 border border-slate-200 rounded-xl text-sm resize-none"
                      rows={3}
                      placeholder="We're currently performing scheduled maintenance. We'll be back shortly!"
                      data-testid="maintenance-message"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
                  <strong>Note:</strong> The following routes remain accessible during maintenance:
                  <ul className="mt-2 space-y-1 text-blue-700 text-xs">
                    <li><code>/admin</code> — Admin Dashboard (Clerk)</li>
                    <li><code>/pos</code> — POS System (Clerk)</li>
                    <li><code>/staff</code> — Staff Portal (JWT)</li>
                    <li><code>/services</code> — Service Admin Portal (JWT)</li>
                    <li><code>/partner</code> — Partner Portal (JWT)</li>
                    <li><code>/kitchen</code> & <code>/beverages</code> — Kitchen & Bar Displays</li>
                  </ul>
                </div>

                {/* Service Toggles */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6">
                  <h3 className="font-bold text-lg mb-4">Service Controls</h3>
                  <p className="text-sm text-slate-500 mb-6">Turn services on or off. When off, customers see an "unavailable" message on the attractions page.</p>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <div className="font-bold text-sm">Tours & Attractions</div>
                        <div className="text-xs text-slate-500">Tour bookings on /attractions</div>
                      </div>
                      <button
                        onClick={() => setSettings({...settings, attractions_enabled: !settings.attractions_enabled})}
                        className={`relative w-14 h-7 rounded-full transition-colors ${settings.attractions_enabled !== false ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        data-testid="attractions-toggle"
                      >
                        <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.attractions_enabled !== false ? 'translate-x-7' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div>
                        <div className="font-bold text-sm">Bike Rentals</div>
                        <div className="text-xs text-slate-500">Rental bookings on /attractions</div>
                      </div>
                      <button
                        onClick={() => setSettings({...settings, rentals_enabled: !settings.rentals_enabled})}
                        className={`relative w-14 h-7 rounded-full transition-colors ${settings.rentals_enabled !== false ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        data-testid="rentals-toggle"
                      >
                        <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.rentals_enabled !== false ? 'translate-x-7' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <button onClick={saveSettings} className="bg-indigo-600 text-white font-bold px-10 py-3.5 rounded-2xl shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all transform active:scale-95">
                {t.saveSettings}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
        <div className="p-6">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
              <LayoutDashboard size={18} className="text-white" />
            </div>
            <span className="tracking-tight">BENZ<span className="text-indigo-600 font-black italic">OS</span></span>
          </h1>
        </div>

        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          {navItems.map(item => {
            if (item.children) {
              const isChildActive = item.children.some(c => c.id === activeTab);
              const isOpen = businessOpen || isChildActive;
              return (
                <div key={item.id}>
                  <button
                    onClick={() => setBusinessOpen(!isOpen)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all ${
                      isChildActive ? 'text-indigo-600' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="nav-business"
                  >
                    <item.icon size={18} className={isChildActive ? 'text-indigo-600' : 'text-slate-400'} />
                    {item.label}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`}><path d="M6 9l6 6 6-6"/></svg>
                  </button>
                  {isOpen && (
                    <div className="ml-4 mt-1 space-y-0.5">
                      {item.children.map(child => {
                        const ChildIcon = child.icon;
                        return (
                          <button
                            key={child.id}
                            onClick={() => setActiveTab(child.id)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium rounded-lg transition-all ${
                              activeTab === child.id ? 'bg-indigo-50 text-indigo-600' : 'text-slate-500 hover:bg-slate-50'
                            }`}
                            data-testid={`nav-${child.id}`}
                          >
                            <ChildIcon size={15} className={activeTab === child.id ? 'text-indigo-600' : 'text-slate-400'} />
                            {child.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const IconComponent = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all ${
                  activeTab === item.id ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <IconComponent size={18} className={activeTab === item.id ? 'text-indigo-600' : 'text-slate-400'} />
                {item.label}
                {item.id === 'orders' && orders.filter(o => o.status === 'pending').length > 0 && (
                  <span className="ml-auto w-2 h-2 rounded-full bg-red-500"></span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 p-2">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center font-bold text-indigo-600">
              {user?.firstName?.[0] || 'A'}
            </div>
            <div>
              <p className="text-sm font-bold truncate w-32">{user?.firstName || 'Admin'}</p>
              <p className="text-xs text-slate-500">Admin</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 px-8 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-6">
            <h2 className="font-bold text-lg capitalize">{t[activeTab]}</h2>
            
            {/* Translation Switcher */}
            <div className="bg-slate-100 p-1 rounded-lg flex items-center gap-1 border border-slate-200">
              <button onClick={() => setAdminLang('en')} className={`px-2.5 py-1 text-[11px] font-extrabold rounded-md transition-all ${adminLang === 'en' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-white'}`}>ENG</button>
              <button onClick={() => setAdminLang('vi')} className={`px-2.5 py-1 text-[11px] font-extrabold rounded-md transition-all ${adminLang === 'vi' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:bg-white'}`}>VN</button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button className="p-2 text-slate-400 hover:text-slate-600"><Search size={20} /></button>
            <button className="p-2 text-slate-400 hover:text-slate-600"><Bell size={20} /></button>
            <div className="h-6 w-px bg-slate-200"></div>
            <button onClick={() => signOut()} className="text-sm font-semibold text-red-500 flex items-center gap-2" data-testid="sign-out-btn">
              <LogOut size={16} />
              {t.logout}
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-8">
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-24" data-testid="admin-tab-loader">
                <div className="flex items-center gap-3 text-slate-400 font-medium">
                  <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  <span>Loading tab...</span>
                </div>
              </div>
            }
          >
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'orders' && renderOrders()}
            {activeTab === 'menu' && renderMenu()}
            {activeTab === 'tables' && renderTables()}
            {activeTab === 'banners' && renderBanners()}
            {activeTab === 'staff' && renderStaff()}
            {activeTab === 'blog' && renderBlog()}
            {activeTab === 'screens' && renderScreens()}
            {activeTab === 'reports' && <Analytics adminLang={adminLang} />}
            {activeTab === 'settings' && renderSettings()}
          </Suspense>
        </div>
      </main>

      {/* Blog Editor Modal */}
      {showBlogEditor && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="blog-editor-modal">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">{editingBlog ? t.editPost : t.addPost}</h2>
              <button onClick={() => setShowBlogEditor(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t.blogTitle}</label>
                <input
                  type="text"
                  value={blogForm.title}
                  onChange={e => setBlogForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                  placeholder="Enter blog title..."
                  data-testid="blog-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t.blogExcerpt}</label>
                <input
                  type="text"
                  value={blogForm.excerpt}
                  onChange={e => setBlogForm(f => ({ ...f, excerpt: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                  placeholder="Brief summary..."
                  data-testid="blog-excerpt-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t.blogTags}</label>
                <input
                  type="text"
                  value={blogForm.tags}
                  onChange={e => setBlogForm(f => ({ ...f, tags: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                  placeholder="Phu Quoc, Food, Travel tips"
                  data-testid="blog-tags-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Author</label>
                  <input
                    type="text"
                    value={blogForm.author}
                    onChange={e => setBlogForm(f => ({ ...f, author: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    placeholder="Linh Nguyen"
                    data-testid="blog-author-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Read Time</label>
                  <input
                    type="text"
                    value={blogForm.read_time}
                    onChange={e => setBlogForm(f => ({ ...f, read_time: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                    placeholder="7 min read"
                    data-testid="blog-readtime-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t.blogContent}</label>
                <textarea
                  value={blogForm.content}
                  onChange={e => setBlogForm(f => ({ ...f, content: e.target.value }))}
                  rows={12}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none font-mono"
                  placeholder="Write your blog post in Markdown..."
                  data-testid="blog-content-input"
                />
                <p className="text-xs text-slate-400 mt-1">Supports Markdown: **bold**, *italic*, # headings, - lists, etc.</p>
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={blogForm.published}
                    onChange={e => setBlogForm(f => ({ ...f, published: e.target.checked }))}
                    className="rounded"
                    data-testid="blog-published-checkbox"
                  />
                  <span className="text-sm font-medium">{t.published}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={blogForm.featured}
                    onChange={e => setBlogForm(f => ({ ...f, featured: e.target.checked }))}
                    className="rounded"
                    data-testid="blog-featured-checkbox"
                  />
                  <span className="text-sm font-medium">Featured (Hero)</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowBlogEditor(false)}
                  className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-xl"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={saveBlogPost}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-indigo-700 transition-colors"
                  data-testid="blog-save-btn"
                >
                  <Save size={16} /> {editingBlog ? t.edit : t.addPost}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {/* Edit Item Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Edit Menu Item</h2>
              <button onClick={() => setEditingItem(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            {/* Image Preview & Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Item Image</label>
              <div className="relative">
                {editingItem.image_url ? (
                  <div className="relative group">
                    <img 
                      src={editingItem.image_url.startsWith('/api') ? `${API_URL}${editingItem.image_url}` : editingItem.image_url} 
                      alt="Preview" 
                      className="w-full h-40 object-cover rounded-xl border border-slate-200" 
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                      <button 
                        onClick={() => imageInputRef.current?.click()}
                        className="px-3 py-2 bg-white text-slate-800 rounded-lg text-sm font-bold hover:bg-slate-100"
                      >
                        Change
                      </button>
                      <button 
                        onClick={() => setEditingItem({...editingItem, image_url: ''})}
                        className="px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    onClick={() => imageInputRef.current?.click()}
                    className="w-full h-40 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50/50 transition-all"
                  >
                    <Upload size={32} className="text-slate-400 mb-2" />
                    <span className="text-sm text-slate-500 font-medium">Click to upload image</span>
                    <span className="text-xs text-slate-400">JPG, PNG, WebP, GIF (max 5MB)</span>
                  </div>
                )}
                <input 
                  type="file" 
                  ref={imageInputRef}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  className="hidden" 
                />
                {imageUploading && (
                  <div className="absolute inset-0 bg-white/80 rounded-xl flex items-center justify-center">
                    <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">English Name *</label>
                <input type="text" value={editingItem.name?.en || ''} onChange={(e) => setEditingItem({...editingItem, name: {...editingItem.name, en: e.target.value}})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                <select value={editingItem.category} onChange={(e) => setEditingItem({...editingItem, category: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl">
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Price (VND) *</label>
                <input type="number" value={editingItem.price} onChange={(e) => setEditingItem({...editingItem, price: parseInt(e.target.value) || 0})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Image URL (or upload above)</label>
                <input type="text" value={editingItem.image_url} onChange={(e) => setEditingItem({...editingItem, image_url: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" placeholder="https://... or upload image above" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Ingredients (comma-separated)</label>
                <input type="text" value={editingItem.ingredients || ''} onChange={(e) => setEditingItem({...editingItem, ingredients: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" placeholder="e.g., Chicken, Rice, Vegetables" />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-3 cursor-pointer flex-1 p-3 bg-slate-50 rounded-xl">
                  <input type="checkbox" checked={editingItem.is_promoted || false} onChange={(e) => setEditingItem({...editingItem, is_promoted: e.target.checked})} className="w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500" />
                  <span className="text-sm font-medium">Must Try</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer flex-1 p-3 bg-slate-50 rounded-xl">
                  <input type="checkbox" checked={editingItem.available !== false} onChange={(e) => setEditingItem({...editingItem, available: e.target.checked})} className="w-5 h-5 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500" />
                  <span className="text-sm font-medium">Available</span>
                </label>
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button onClick={() => setEditingItem(null)} className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-bold hover:bg-slate-50">{t.cancel}</button>
              <button onClick={updateMenuItem} className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showAddItemModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{t.addNewItem}</h2>
            <p className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg mb-4">Enter English name - other languages auto-translated!</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">English Name *</label>
                <input type="text" value={newItem.name} onChange={(e) => setNewItem({...newItem, name: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" placeholder="e.g., Grilled Chicken" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category *</label>
                <select value={newItem.category} onChange={(e) => setNewItem({...newItem, category: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl">
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Price (VND) *</label>
                <input type="number" value={newItem.price} onChange={(e) => setNewItem({...newItem, price: parseInt(e.target.value) || 0})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" placeholder="e.g., 150000" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Image URL *</label>
                <input type="text" value={newItem.image_url} onChange={(e) => setNewItem({...newItem, image_url: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" placeholder="https://..." />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Ingredients (comma-separated)</label>
                <input type="text" value={newItem.ingredients} onChange={(e) => setNewItem({...newItem, ingredients: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" placeholder="e.g., Chicken, Rice, Vegetables" />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-3 cursor-pointer flex-1">
                  <input type="checkbox" checked={newItem.is_promoted} onChange={(e) => setNewItem({...newItem, is_promoted: e.target.checked})} className="w-5 h-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500" />
                  <span className="text-sm font-medium">Must Try Item</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer flex-1">
                  <input type="checkbox" checked={newItem.available !== false} onChange={(e) => setNewItem({...newItem, available: e.target.checked})} className="w-5 h-5 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500" />
                  <span className="text-sm font-medium">Available</span>
                </label>
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button onClick={() => setShowAddItemModal(false)} className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-bold hover:bg-slate-50">{t.cancel}</button>
              <button onClick={saveNewItem} disabled={!newItem.name || !newItem.image_url || newItem.price <= 0} className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50">Add Item</button>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">{adminLang === 'en' ? 'Manage Categories' : 'Quản lý danh mục'}</h2>
              <button onClick={() => setShowCategoryModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            {/* Add New Category */}
            <div className="flex gap-2 mb-6">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder={adminLang === 'en' ? 'New category name...' : 'Tên danh mục mới...'}
                className="flex-1 px-4 py-3 border border-slate-200 rounded-xl"
                onKeyPress={(e) => e.key === 'Enter' && addCategory()}
              />
              <button
                onClick={addCategory}
                className="px-4 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700"
              >
                <Plus size={20} />
              </button>
            </div>

            {/* Category List */}
            <div className="space-y-2">
              {categories.map(cat => {
                const itemCount = menuItems.filter(item => item.category === cat).length;
                return (
                  <div key={cat} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    {editingCategory === cat ? (
                      <input
                        type="text"
                        defaultValue={cat}
                        autoFocus
                        className="flex-1 px-3 py-2 border border-indigo-300 rounded-lg mr-2"
                        onBlur={(e) => renameCategory(cat, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            renameCategory(cat, e.target.value);
                          }
                        }}
                      />
                    ) : (
                      <div className="flex items-center gap-3">
                        <Tag size={16} className="text-slate-400" />
                        <span className="font-medium text-slate-700">{cat}</span>
                        <span className="text-xs text-slate-400 bg-slate-200 px-2 py-0.5 rounded-full">
                          {itemCount} {adminLang === 'en' ? 'items' : 'món'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingCategory(editingCategory === cat ? null : cat)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => deleteCategory(cat)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200">
              <button
                onClick={() => setShowCategoryModal(false)}
                className="w-full px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold hover:bg-slate-200"
              >
                {adminLang === 'en' ? 'Done' : 'Xong'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Table Modal */}
      {showAddTableModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">{t.addNewTable}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Table Number *</label>
                <input type="text" value={newTable.number} onChange={(e) => setNewTable({...newTable, number: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" placeholder="e.g., 1, A1, B2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Capacity *</label>
                <input type="number" value={newTable.capacity} onChange={(e) => setNewTable({...newTable, capacity: parseInt(e.target.value)})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" min="1" max="20" />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button onClick={() => setShowAddTableModal(false)} className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-bold hover:bg-slate-50">{t.cancel}</button>
              <button onClick={saveNewTable} disabled={!newTable.number.trim() || newTable.capacity <= 0} className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50">Add Table</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Table Modal */}
      {editingTable && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Edit Table</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Table Number *</label>
                <input type="text" value={editingTable.number} onChange={(e) => setEditingTable({...editingTable, number: e.target.value})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Capacity *</label>
                <input type="number" value={editingTable.capacity} onChange={(e) => setEditingTable({...editingTable, capacity: parseInt(e.target.value)})} className="w-full px-4 py-3 border border-slate-200 rounded-xl" min="1" max="20" />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button onClick={() => setEditingTable(null)} className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-bold hover:bg-slate-50">{t.cancel}</button>
              <button onClick={updateTable} disabled={!editingTable.number.trim() || editingTable.capacity <= 0} className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style dangerouslySetInnerHTML={{ __html: `
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #94A3B8; }
      `}} />
    </div>
  );
};

export default AdminDashboard;
